import React, { useState, useEffect, useMemo, useRef } from "react";
import { Badge, Button, Form, Table, Modal } from "react-bootstrap";
import { Autocomplete, Box, TextField, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import { Add } from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DOMPurify from "dompurify";
import TextEditor from "components/TextEditor/TextEditor";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles, getDisabledInputStyles, DETAILS_BG, filterPopoverStyles, tableStyles } from "Constants/Style";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { fetchSacCodeByDropDown } from "slices/Masters/sac-code/thunk";
import { fetchGstByDropDown } from "slices/Masters/gst/thunk";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import ProductHistoryModal from "Common/Components/ProductHistoryModal/ProductHistoryModal";
import AddShippingAddressModal from "pages/QuotationV2/Form/Components/AddShippingAddressModal";
import NoSelectedProducts from "assets/images/Order/NoSelectedProducts.png";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import AddServiceAccessoriesModal from "./Components/AddServiceAccessoriesModal";
import ViewServiceAccessoriesModal from "./Components/ViewServiceAccessoriesModal";
import { recalcAccessory } from "./Components/ServiceAccessoryRow";
import { AccessoryItem } from "./Components/ServiceAccessoryRow";

interface EnquiryServiceFormProps {
    onAdd: (values: any) => void;
    items: any[];
    setItems: React.Dispatch<React.SetStateAction<any[]>>;
    shippingAddresses: any; // Added company prop
    setShowAddressModal?: React.Dispatch<React.SetStateAction<boolean>>; // Optional, using internal modal now
    billFromAddress: any;
    composition_gst: any;
    select_composition_gst: any;
    currency_code?: string;
    currency_symbol?: string;
    exchange_rate?: string;
    company_gst_type: any;
    billToAddress: any;
    companyId?: string | null; // Company ID for adding shipping addresses
    company?: any; // Company object for formik updates (optional)
}

type ServiceFormValues = {
    model: string;
    model_name: string;
    serviceName: string;
    serialNo: string;
    serviceCategory: string;
    serviceMethod: string;
    serviceType: string;
    executionMode: string;
    sac_code: string; // SAC code field
    sac_code_id: string; // SAC code ID
    productQuantity: string;
    serviceRate: string; // Renamed from serviceAmount
    totalServiceAmount: string; // New field
    serviceAddress: string | any; // Can be string (legacy) or object with shipping address details
    shipping_address_id: string;
    reportedComplaint: string;
    // Additional fields for payload
    category: string;
    category_name: string;
    sub_category: string;
    sub_category_name: string;
    make: string;
    make_name: string;
    unit: string;
    unit_name: string;
    item: string;
    item_name: string;
    sku: string;
    product_sku: string;
    hsn: string;
    hsn_code: string;
    discount: number;
    discount_type: "%" | "₹";
    discount_value: number;
    gst_percent: number;
    gst_percentage: number;
    original_gst_percent: number;
    gst_value: number;
    net_value: number;
    cgst_percent: number;
    sgst_percent: number;
    igst_percent: number;
    product_type: any[];
    [key: string]: any;
};

const commonInputStyle = {
    fontFamily: "Poppins, sans-serif",
    fontSize: "13px",
};

const EnquiryServiceForm: React.FC<EnquiryServiceFormProps> = ({
    onAdd,
    items,
    setItems,
    setShowAddressModal,
    billFromAddress,
    shippingAddresses,
    composition_gst,
    select_composition_gst,
    currency_code,
    currency_symbol,
    exchange_rate,
    company_gst_type,
    billToAddress,
    companyId,
    company,
}) => {
    const dispatch = useDispatch<any>();

    const initialFormState: ServiceFormValues = {
        model: "",
        model_name: "",
        serviceName: "",
        serialNo: "",
        serviceCategory: "",
        serviceMethod: "",
        serviceType: "",
        executionMode: "",
        sac_code: "",
        sac_code_id: "",
        productQuantity: "1",
        serviceRate: "",
        totalServiceAmount: "", // Initial empty
        serviceAddress: "",
        reportedComplaint: "",
        shipping_address_id: "",
        category: "",
        category_name: "",
        sub_category: "",
        sub_category_name: "",
        make: "",
        make_name: "",
        unit: "",
        unit_name: "",
        item: "",
        item_name: "",
        sku: "",
        product_sku: "",
        hsn: "",
        hsn_code: "",
        discount: 0,
        discount_type: "%",
        discount_value: 0,
        gst_percent: 0,
        gst_percentage: 0,
        original_gst_percent: 0,
        gst_value: 0,
        net_value: 0,
        cgst_percent: 0,
        sgst_percent: 0,
        igst_percent: 0,
        product_type: [],
        prod_description: "",
        additional_details: "",
    };

    const [formData, setFormData] = useState<ServiceFormValues>(initialFormState);
    const [modelOptions, setModelOptions] = useState<any[]>([]);
    const [serviceOptions, setServiceOptions] = useState<any[]>([]);
    const [selectedModel, setSelectedModel] = useState<any>(null);
    const [selectedService, setSelectedService] = useState<any>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [sacCodeErrors, setSacCodeErrors] = useState<{ [key: number]: string }>({}); // Track SAC code errors by item index
    const [serialNoErrors, setSerialNoErrors] = useState<{ [key: number]: string }>({}); // Track Serial No. errors by item index
    const [serviceAddressOptions, setServiceAddressOptions] = useState<any[]>([]);
    const [sacCodeOptions, setSacCodeOptions] = useState<any[]>([]);
    const [gstOptions, setGstOptions] = useState<any[]>([]);
    const [selectedSacCode, setSelectedSacCode] = useState<any>(null);
    const [selectedGst, setSelectedGst] = useState<any>(null);

    // Ref to track previous items to detect when items are loaded from backend
    const prevItemsRef = useRef<any[]>([]);
    // Ref to track if we've already matched SAC codes to prevent infinite loops
    const sacCodeMatchedRef = useRef<string>("");
    // Ref to track if we're currently matching SAC codes (to prevent validation during matching)
    const isMatchingRef = useRef<boolean>(false);

    // Shipping Address Modal State
    const [showShippingAddressModal, setShowShippingAddressModal] = useState(false);

    // Product History Modal State
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedSerialNo, setSelectedSerialNo] = useState<string>("");
    const [productHistory, setProductHistory] = useState<any>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [showDescriptionModal, setShowDescriptionModal] = useState(false);
    const [descriptionModalContent, setDescriptionModalContent] = useState("");

    // Accessories modals
    const [showAddAccessoriesModal, setShowAddAccessoriesModal] = useState(false);
    const [showViewAccessoriesModal, setShowViewAccessoriesModal] = useState(false);
    const [accessoriesServiceIndex, setAccessoriesServiceIndex] = useState<number | null>(null);
    const [descriptionModalServiceName, setDescriptionModalServiceName] = useState("");
    const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
    const [additionalDetailsIndex, setAdditionalDetailsIndex] = useState<number | null>(null);
    const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState("");

    // Fetch product history
    const fetchProductHistory = async (serialNo: string) => {
        if (!serialNo || serialNo.trim() === "" || serialNo === "N/A") {
            toast.error("Invalid serial number");
            return;
        }

        setLoadingHistory(true);
        setSelectedSerialNo(serialNo.trim());
        setShowHistoryModal(true);

        try {
            const response = await Axios.get(
                API_ENDPOINT.INVENTORY.PRODUCT_HISTORY(serialNo.trim())
            );
            setProductHistory(response.data?.data || response.data);
        } catch (error: any) {
            toast.error(
                error.response?.data?.message ||
                "Failed to fetch product history"
            );
            setProductHistory(null);
        } finally {
            setLoadingHistory(false);
        }
    };

    // GST calculation helpers
    const isCompanyCompositionalGstType = useMemo(() => {
        const raw = (company_gst_type || "").toString().trim().toLowerCase();
        if (!raw) return false;
        // Backend may send variations like "Compositional gst", "Composition GST", etc.
        // Make sure it's specifically compositional/composition GST, not just containing the word
        const isCompositional = raw === "compositional gst" || raw === "composition gst" || raw === "compositional" || raw === "composition";
        return isCompositional;
    }, [company_gst_type]);

    // If company gst_type is compositional, no GST should be applied on any item (products + services)
    const shouldZeroGstForCompany = isCompanyCompositionalGstType;
    // console.log("items => ", items)
    const normalizeState = (s: any) => (s || "").toString().trim().toLowerCase();
    const billFromState = useMemo(
        () => normalizeState(billFromAddress?.org_state || billFromAddress?.state),
        [billFromAddress]
    );
    const billToState = useMemo(
        () => normalizeState(billToAddress?.state),
        [billToAddress]
    );
    const isIntraState =
        Boolean(billFromState) &&
        Boolean(billToState) &&
        billFromState === billToState;

    const roundToTwoDecimals = (num: number): number => {
        return Math.round(num * 100) / 100;
    };

    // Check if items need discount_amount recalculation
    const needsDiscountRecalculation = useMemo(() => {
        return items.some((item) =>
            item.discount_value !== undefined &&
            item.discount_value !== null &&
            item.discount_type !== undefined &&
            item.discount_type !== null &&
            (item.discount_amount === undefined || item.discount_amount === null)
        );
    }, [items]);

    // Load Service Address Options based on Company
    useEffect(() => {
        if (shippingAddresses && Array.isArray(shippingAddresses)) {
            const formattedOptions = shippingAddresses.map((addr: any) => ({
                id: addr.shipping_address_id || addr.id,
                shipping_address_id: addr.shipping_address_id || addr.id,
                name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode || addr.pin_code}`,
                address: addr.address,
                city: addr.city,
                state: addr.state,
                country: addr.country,
                pincode: addr.pincode || addr.pin_code,
                phone_number: addr.phone_number,
                phone_country: addr.phone_country,
                company_id: addr.company_id,
                is_default: addr.is_default,
                created_at: addr.created_at,
                updated_at: addr.updated_at,
                // Store the full address object for reference
                fullAddress: addr
            }));
            setServiceAddressOptions(formattedOptions);
        } else {
            // Fallback or empty if no company selected
            setServiceAddressOptions([]);
        }
    }, [shippingAddresses]);

    // Recalculate discount_amount and other values when items are loaded or currency changes
    useEffect(() => {
        if (!Array.isArray(items) || items.length === 0) {
            prevItemsRef.current = items;
            return;
        }

        // Check if any item needs recalculation (missing discount_amount or currency changed)
        const needsRecalculation = items.some((item) => {
            // If discount_amount is missing but we have discount_value and discount_type, recalculate
            const hasDiscountValue = item.discount_value !== undefined && item.discount_value !== null;
            const hasDiscountType = item.discount_type !== undefined && item.discount_type !== null;
            const hasDiscountAmount = item.discount_amount !== undefined && item.discount_amount !== null;

            if (hasDiscountValue && hasDiscountType && !hasDiscountAmount) {
                return true;
            }

            return false;
        });

        // Check if currency or exchange rate changed
        const currencyChanged =
            currency_code !== prevItemsRef.current[0]?.currency_code ||
            exchange_rate !== prevItemsRef.current[0]?.exchange_rate;

        // Check if items length changed or items are different
        const itemsChanged =
            prevItemsRef.current.length !== items.length ||
            items.some((item, index) => {
                const prevItem = prevItemsRef.current[index];
                return !prevItem || prevItem.id !== item.id;
            });

        // Only recalculate if needed
        if (!needsRecalculation && !currencyChanged && !itemsChanged && prevItemsRef.current.length > 0) {
            return;
        }

        const updated = items.map((item) => {
            const qty = Number(item.productQuantity || item.quantity) || 0;
            const rate = Number(item.serviceRate || item.unit_price) || 0;

            // Calculate gross value based on currency
            let itemTotal = 0;
            let grossValue = 0;
            const currentCurrencyCode = item.currency_code || currency_code || "INR";
            const currentExchangeRate = item.exchange_rate || exchange_rate || 1;

            if (currentCurrencyCode !== "INR" && currentCurrencyCode !== "IND") {
                // For foreign currency, use rate_in_currency
                const rateInCurrency = item.rate_in_currency
                    ? Number(item.rate_in_currency)
                    : (Number(currentExchangeRate) > 0
                        ? (Number(rate) / Number(currentExchangeRate))
                        : Number(rate));
                itemTotal = roundToTwoDecimals(qty * rateInCurrency);
                grossValue = itemTotal;
            } else {
                // For INR, use serviceRate/unit_price
                itemTotal = roundToTwoDecimals(qty * rate);
                grossValue = itemTotal;
            }

            // Calculate rate_in_currency if exchange_rate is available
            let rateInCurrency = "";
            if (currentExchangeRate && Number(currentExchangeRate) > 0) {
                rateInCurrency = (Number(rate) / Number(currentExchangeRate)).toFixed(2);
            } else if (item.rate_in) {
                rateInCurrency = Number(item.rate_in).toFixed(2);
            }

            // Calculate discount_amount based on discount_type and discount_value
            let discountValue = Number(item.discount_value) || 0;
            let discountAmount = 0;
            const discountType = item.discount_type || "%";

            // Use grossValue for discount calculations
            const grossValueForDiscount = Number(item.gross_value) || grossValue || itemTotal;

            // Check if discount_type is percentage (could be "%" or "percent")
            const isPercentageDiscount = discountType === "%" || discountType === "percent" || discountType?.toLowerCase() === "percent";

            if (isPercentageDiscount) {
                // Validate: cap discount_value at 100%
                if (discountValue > 100) {
                    discountValue = 100;
                }
                // Calculate discount_amount from percentage (for display only)
                discountAmount = grossValueForDiscount
                    ? roundToTwoDecimals((grossValueForDiscount * discountValue) / 100)
                    : 0;
            } else {
                // For fixed amount (currency), discount_value IS the discount_amount
                // Validate: discount_value cannot exceed gross value
                if (discountValue > grossValueForDiscount) {
                    discountValue = grossValueForDiscount;
                }
                // For currency, discount_amount equals discount_value (for display)
                discountAmount = roundToTwoDecimals(discountValue);
            }

            // Calculate net value
            const netValue = roundToTwoDecimals(grossValueForDiscount - discountAmount);

            // Calculate GST
            let gstValue = 0;
            const gstPercentForCalc = shouldZeroGstForCompany ? 0 : (Number(item.original_gst_percent) || Number(item.gst_percentage) || Number(item.gst_percent) || 0);

            if (!shouldZeroGstForCompany && (currentCurrencyCode === "INR" || currentCurrencyCode === "IND")) {
                if (item.composition_gst && item.select_composition_gst) {
                    const compGstPercent = Number(item.select_composition_gst) || 0;
                    gstValue = netValue > 0 ? roundToTwoDecimals((netValue * compGstPercent) / 100) : 0;
                } else {
                    gstValue = netValue > 0 ? roundToTwoDecimals((netValue * gstPercentForCalc) / 100) : 0;
                }
            }

            // Calculate final total
            const finalTotal = roundToTwoDecimals(netValue + gstValue);

            // Recalculate GST split
            let cgstPercent = 0;
            let sgstPercent = 0;
            let igstPercent = 0;

            if (shouldZeroGstForCompany || (currentCurrencyCode !== "INR" && currentCurrencyCode !== "IND")) {
                cgstPercent = 0;
                sgstPercent = 0;
                igstPercent = 0;
            } else if (!billFromState || !billToState) {
                cgstPercent = 0;
                sgstPercent = 0;
                igstPercent = gstPercentForCalc;
            } else if (billFromState === billToState) {
                cgstPercent = gstPercentForCalc / 2;
                sgstPercent = gstPercentForCalc / 2;
                igstPercent = 0;
            } else {
                cgstPercent = 0;
                sgstPercent = 0;
                igstPercent = gstPercentForCalc;
            }

            return {
                ...item,
                currency_code: currentCurrencyCode,
                exchange_rate: currentExchangeRate,
                rate_in_currency: rateInCurrency || item.rate_in_currency,
                gross_value: grossValue.toFixed(2),
                discount_value: discountValue,
                discount_amount: discountAmount.toFixed(2),
                discount_percent: (discountType === "%" || discountType === "percent" || discountType?.toLowerCase() === "percent") ? discountValue : null,
                net_value: netValue.toFixed(2),
                gst_percent: shouldZeroGstForCompany ? 0 : gstPercentForCalc,
                gst_value: gstValue.toFixed(2),
                cgst_percent: cgstPercent,
                sgst_percent: sgstPercent,
                igst_percent: igstPercent,
                total_amount: finalTotal.toFixed(2), // Final total (net + GST) - used for total_service_amount
                totalServiceAmount: finalTotal.toFixed(2), // Final total (net + GST)
                total: grossValue.toFixed(2),
                accessories: Array.isArray(item.accessories)
                    ? item.accessories.map((acc: any) =>
                        recalcAccessory(
                            {
                                ...acc,
                                gst_percentage: shouldZeroGstForCompany
                                    ? 0
                                    : Number(acc.gst_percentage || 0),
                            },
                            shouldZeroGstForCompany,
                            Boolean(billFromState) &&
                                Boolean(billToState) &&
                                billFromState === billToState,
                        ),
                    )
                    : item.accessories,
            };
        });

        setItems(updated);
        prevItemsRef.current = updated;
    }, [
        items.length,
        currency_code,
        exchange_rate,
        shouldZeroGstForCompany,
        billFromState,
        billToState,
        needsDiscountRecalculation,
    ]);

    // Validation Schema
    const validationSchema = Yup.object().shape({
        model: Yup.string().required("Model is required"),
        serviceName: Yup.string().required("Service is required"),
        serialNo: Yup.string().required("Serial No. is required"),
        serviceCategory: Yup.string().required("Service Category is required"),

        serviceMethod: Yup.string()
            .when("serviceCategory", {
                is: (val: string) => ["RMA", "Testing", "Repair"].includes(val),
                then: (schema) => schema.notRequired(),
                otherwise: (schema) => schema
                    .required("Service Method is required")
                    .test("not-na", "Service Method is required", (value) => value !== "NA" && value !== ""),
            }),

        serviceType: Yup.string()
            .when("serviceCategory", {
                is: (val: string) => ["RMA", "Testing", "Repair", "Installation", "Others"].includes(val),
                then: (schema) => schema.notRequired(),
                otherwise: (schema) => schema
                    .required("Service Type is required")
                    .test("not-na", "Service Type is required", (value) => value !== "NA" && value !== ""),
            }),

        executionMode: Yup.string()
            .when("serviceCategory", {
                is: (val: string) => ["RMA", "Testing"].includes(val),
                then: (schema) => schema.notRequired(),
                otherwise: (schema) => schema
                    .required("Execution Mode is required")
                    .test("not-na", "Execution Mode is required", (value) => value !== "NA" && value !== ""),
            }),

        sac_code: Yup.string()
            .required("SAC Code is required")
            .test("sac-code-required", "SAC Code is required", (value) => {
                return value !== undefined && value !== null && value.trim() !== "";
            }),
        sac_code_id: Yup.string()
            .required("SAC Code is required")
            .test("sac-code-id-required", "SAC Code is required", (value) => {
                return value !== undefined && value !== null && value.trim() !== "";
            }),

        productQuantity: Yup.number()
            .transform((value, originalValue) => (originalValue === "" ? undefined : value))
            .required("Product Quantity is required")
            .positive("Quantity must be positive")
            .typeError("Quantity must be a number"),
        serviceRate: Yup.number()
            .transform((value, originalValue) => (originalValue === "" ? undefined : value))
            .required("Service Rate is required")
            .min(0, "Rate cannot be negative")
            .typeError("Rate must be a number"),
        serviceAddress: Yup.mixed()
            .required("Service Address is required")
            .test(
                "is-valid-service-address",
                "Service Address is required",
                (value: any) => {
                    if (!value) return false;
                    // Accept object with shipping_address_id
                    if (typeof value === 'object' && (value.shipping_address_id || value.id)) return true;
                    // Accept string for backward compatibility
                    if (typeof value === 'string' && value.trim() !== '') return true;
                    return false;
                }
            ),
        // reportedComplaint is intentionally NOT required
    });

    // Hardcoded dropdown options for services
    const serviceCategoryOptions = [
        { id: 1, name: "Calibration" },
        { id: 2, name: "Calibration & Repair" },
        { id: 3, name: "Repair" },
        { id: 4, name: "Testing" },
        { id: 5, name: "Installation" },
        { id: 6, name: "RMA" },
        { id: 7, name: "Others" },
    ];

    const serviceMethodOptions = [
        { id: 1, name: "Offsite" },
        { id: 2, name: "Onsite" },
    ];

    const serviceTypeOptions = [
        { id: 1, name: "Standard" },
        { id: 2, name: "NABL" },
    ];

    const executionModeOptions = [
        { id: 1, name: "Inhouse" },
        { id: 2, name: "3rd party" },
    ];

    // Fetch Models on Mount
    useEffect(() => {
        dispatch(fetchModelByDropDown())
            .unwrap()
            .then((res: any) => {
                setModelOptions(res?.data || []);
            })
            .catch((err: any) => {
                // console.error("Error fetching models:", err);
                toast.error("Failed to fetch models");
            });
    }, [dispatch]);

    // Fetch SAC Code and GST Dropdowns on Mount
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const [sacRes, gstRes] = await Promise.all([
                    dispatch(fetchSacCodeByDropDown()).unwrap(),
                    dispatch(fetchGstByDropDown()).unwrap(),
                ]);
                setSacCodeOptions(sacRes?.data || []);
                setGstOptions(gstRes?.data || []);
            } catch (err: any) {
                // console.error("Error fetching dropdowns:", err);
                toast.error("Failed to fetch dropdown options");
            }
        };
        fetchDropdowns();
    }, [dispatch]);

    // Match sac_code to sac_code_id when items are loaded from backend
    useEffect(() => {
        if (items && items.length > 0 && sacCodeOptions && sacCodeOptions.length > 0) {
            // Create a unique key for this batch of items to track if we've processed them
            const itemsKey = items.map((item: any) => `${item.id || item.item || ''}_${item.sac_code || ''}`).join('|');
            
            // Skip if we've already processed these exact items
            if (sacCodeMatchedRef.current === itemsKey) {
                return;
            }
            
            // Set flag to indicate we're matching
            isMatchingRef.current = true;
            
            let hasChanges = false;
            const updatedItems = items.map((item: any, index: number) => {
                // If item has sac_code but no sac_code_id, try to find it from options
                if (item.sac_code && (!item.sac_code_id || item.sac_code_id.trim() === "")) {
                    // Normalize the sac_code for comparison
                    const itemSacCode = String(item.sac_code).trim();
                    
                    const matchedSacCode = sacCodeOptions.find((sac: any) => {
                        // Try multiple matching strategies with normalized values
                        const sacCodeValue = String(sac.sac_code || "").trim();
                        return sacCodeValue === itemSacCode || 
                            sacCodeValue === String(item.sac_code) ||
                            String(sac.sac_code) === String(item.sac_code);
                    });
                    
                    if (matchedSacCode) {
                        const matchedId = matchedSacCode.sac_code_id || matchedSacCode.id || "";
                        if (matchedId && matchedId !== item.sac_code_id) {
                            hasChanges = true;
                            // Clear validation error for this item since we found a match
                            setSacCodeErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors[index];
                                return newErrors;
                            });
                            return {
                                ...item,
                                sac_code_id: matchedId
                            };
                        }
                    }
                }
                return item;
            });
            
            if (hasChanges) {
                sacCodeMatchedRef.current = itemsKey;
                setItems(updatedItems);
                // Validate after state update completes
                setTimeout(() => {
                    isMatchingRef.current = false;
                    validateAllSacCodes();
                    validateAllSerialNos();
                }, 0);
            } else {
                // Mark as processed even if no changes (to avoid re-processing)
                sacCodeMatchedRef.current = itemsKey;
                isMatchingRef.current = false;
                // Still validate to ensure errors are cleared if items already have sac_code_id
                validateAllSacCodes();
                validateAllSerialNos();
            }
        }
    }, [sacCodeOptions.length, items.length, sacCodeOptions]);

    // Validate all SAC codes and Serial Nos. when items change (but only if SAC code options are loaded and matching is not in progress)
    useEffect(() => {
        if (items && items.length > 0 && sacCodeOptions && sacCodeOptions.length > 0 && !isMatchingRef.current) {
            // Check if we've processed the items (matching has been attempted)
            const itemsKey = items.map((item: any) => `${item.id || item.item || ''}_${item.sac_code || ''}`).join('|');
            // Only validate if we've already attempted matching (to avoid validating before matching completes)
            if (sacCodeMatchedRef.current === itemsKey || sacCodeMatchedRef.current === "") {
                validateAllSacCodes();
                validateAllSerialNos();
            }
        }
    }, [items.length, sacCodeOptions.length]);

    const handleModelSelect = (_: any, selectedModel: any | null) => {
        setSelectedModel(selectedModel);

        if (!selectedModel) {
            handleFormReset();
            return;
        }

        const model_id = selectedModel.model_id;

        setFormData((prev) => ({
            ...prev,
            model: model_id,
            model_name: selectedModel.model_name,
            serviceName: "",
            item: "",
            item_name: "",
        }));

        setSelectedService(null);
        setServiceOptions([]);
        setTimeout(() => setErrors((prev) => ({ ...prev, model: "", serviceName: "" })), 0);

        dispatch(fetchProductsDropDownByModel(model_id))
            .unwrap()
            .then((payload: any) => {
                const services = Array.isArray(payload.data) ? payload.data : [];
                setServiceOptions(services);
            })
            .catch(() => toast.error("Error fetching services for selected model"));
    };

    const handleServiceSelect = (_: any, selectedService: any) => {
        setSelectedService(selectedService);

        if (selectedService) {
            const originalGstPercent = selectedService.gst_percent || selectedService.hsn?.gst?.gst_percentage || 0;
            const gstPercent = shouldZeroGstForCompany ? 0 : originalGstPercent;

            // Calculate GST split
            let cgstPercent = 0;
            let sgstPercent = 0;
            let igstPercent = 0;

            if (!shouldZeroGstForCompany) {
                if (!billFromState || !billToState) {
                    igstPercent = originalGstPercent;
                } else if (billFromState === billToState) {
                    cgstPercent = originalGstPercent / 2;
                    sgstPercent = originalGstPercent / 2;
                } else {
                    igstPercent = originalGstPercent;
                }
            }

            setFormData((prev) => ({
                ...prev,
                item: selectedService.id || selectedService.product_id || selectedService.item_id || "",
                serviceName: selectedService.product_name || selectedService.item_name || "",
                item_name: selectedService.product_name || selectedService.item_name || "",
                // serviceRate: NO AUTOFILL requested.
                // Resetting to empty or keeping previous?
                // "add service rate and it will be editable not autofill" implies start blank.
                serviceRate: "",
                totalServiceAmount: "",
                category: selectedService.category?.category_id || selectedService.category?.category || "",
                category_name: selectedService.category?.category_name || "",
                sub_category:
                    selectedService.sub_category?.sub_category_id ||
                    selectedService.sub_category?.sub_category ||
                    "",
                sub_category_name: selectedService.sub_category?.sub_category_name || "",
                make: selectedService.make?.make_id || "",
                make_name: selectedService.make?.make_name || "",
                model: selectedService.model?.model_id || prev.model,
                model_name: selectedService.model?.model_name || prev.model_name,
                hsn: selectedService.hsn_id || selectedService.hsn?.hsn_code_id || "",
                hsn_code: selectedService.hsn?.hsn_code || "",
                unit: selectedService.unit?.unit_id || selectedService.unit?.unit || "",
                unit_name: selectedService.unit?.unit_name || "",
                sku: selectedService.sku || selectedService.item_sku || selectedService.product_sku || "",
                product_sku: selectedService.product_sku || selectedService.sku || selectedService.item_sku || "",
                discount: 0,
                discount_type: "%",
                discount_value: 0,
                gst_percent: gstPercent,
                gst_percentage: selectedService.hsn?.gst?.gst_percentage || selectedService.gst_percent || 0,
                original_gst_percent: originalGstPercent,
                gst_value: 0,
                net_value: 0,
                cgst_percent: cgstPercent,
                sgst_percent: sgstPercent,
                igst_percent: igstPercent,
                product_type: selectedService.product_types || [],
                prod_description: selectedService.prod_description ?? selectedService.description ?? "",
                additional_details: selectedService.additional_details ?? "",
            }));
            setTimeout(() => setErrors((prev) => ({ ...prev, serviceName: "" })), 0);
        } else {
            setFormData((prev) => ({
                ...prev,
                item: "",
                serviceName: "",
                item_name: "",
                serviceRate: "",
                totalServiceAmount: "",
                serviceAddress: "",
                shipping_address_id: "",
                category: "",
                category_name: "",
                sub_category: "",
                sub_category_name: "",
                make: "",
                make_name: "",
                hsn: "",
                hsn_code: "",
                unit: "",
                unit_name: "",
                sku: "",
                product_sku: "",
                discount: 0,
                discount_type: "%",
                discount_value: 0,
                gst_percent: 0,
                gst_percentage: 0,
                original_gst_percent: 0,
                gst_value: 0,
                net_value: 0,
                cgst_percent: 0,
                sgst_percent: 0,
                igst_percent: 0,
                product_type: [],
            }));
        }
    };

    const handleInputChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const updated = { ...prev, [name]: value };

            // If discount_type is being changed, reset discount_value to 0
            if (name === "discount_type" && prev.discount_type !== value) {
                updated.discount_value = 0;
            }

            // Calculate Total Service Amount if Quantity or Rate changes
            if (name === "productQuantity" || name === "serviceRate") {
                const qty = parseFloat(name === "productQuantity" ? value : prev.productQuantity) || 0;
                const rate = parseFloat(name === "serviceRate" ? value : prev.serviceRate) || 0;
                updated.totalServiceAmount = (qty * rate).toFixed(2);
            }

            return updated;
        });

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleFormReset = () => {
        setEditingIndex(null);
        setFormData(initialFormState);
        setServiceOptions([]);
        setSelectedModel(null);
        setSelectedService(null);
        setSelectedSacCode(null);
        setSelectedGst(null);
        setErrors({});
    };

    const handleCancelEdit = () => {
        handleFormReset();
    };

    const handleEdit = (index: number) => {
        const itemToEdit = items[index];
        if (!itemToEdit) return;
        if (itemToEdit.challan_status || itemToEdit.diff_status === "LOCKED") {
            toast.warning("Service item is locked because a Challan has already been generated.");
            return;
        }

        setEditingIndex(index);

        const modelVal = itemToEdit.model || itemToEdit.model_name || "";
        const matchedModel = modelOptions.find(
            (m: any) => m.model_name === modelVal || m.id === modelVal || m.model_id === modelVal
        );
        if (matchedModel) {
            setSelectedModel(matchedModel);
            const modelId = matchedModel.model_id || matchedModel.id;
            if (modelId) {
                dispatch(fetchProductsDropDownByModel(modelId))
                    .unwrap()
                    .then((res: any) => {
                        const services = res?.data || [];
                        setServiceOptions(services);
                        const sName = itemToEdit.serviceName || itemToEdit.item_name || itemToEdit.product_name;
                        const matchedService = services.find(
                            (s: any) => s.product_name === sName || s.item_name === sName || s.id === itemToEdit.item
                        );
                        if (matchedService) {
                            setSelectedService(matchedService);
                        }
                    })
                    .catch(() => {});
            }
        } else {
            setSelectedModel(modelVal ? { model_name: modelVal } : null);
        }

        const sacCodeVal = itemToEdit.sac_code || "";
        const matchedSac = sacCodeOptions.find(
            (s: any) => String(s.sac_code) === String(sacCodeVal) || s.sac_code_id === itemToEdit.sac_code_id
        );
        setSelectedSacCode(matchedSac || (sacCodeVal ? { sac_code: sacCodeVal, sac_code_id: itemToEdit.sac_code_id || "" } : null));

        const gstVal = Number(itemToEdit.gst_percentage || itemToEdit.gst_percent || 0);
        const matchedGst = gstOptions.find(
            (g: any) => Number(g.gst_percentage || g.gst_percent) === gstVal
        );
        setSelectedGst(matchedGst || null);

        setFormData({
            model: modelVal,
            model_name: itemToEdit.model_name || modelVal,
            serviceName: itemToEdit.serviceName || itemToEdit.item_name || itemToEdit.product_name || "",
            serialNo: itemToEdit.serialNo || itemToEdit.serial_no || itemToEdit.serial_number || "",
            serviceCategory: itemToEdit.serviceCategory || itemToEdit.service_category || "",
            serviceMethod: itemToEdit.serviceMethod || itemToEdit.service_method || "",
            serviceType: itemToEdit.serviceType || itemToEdit.service_type || "",
            executionMode: itemToEdit.executionMode || itemToEdit.execution_mode || "",
            sac_code: itemToEdit.sac_code || "",
            sac_code_id: itemToEdit.sac_code_id || itemToEdit.sac || "",
            productQuantity: (itemToEdit.productQuantity ?? itemToEdit.quantity ?? 1).toString(),
            serviceRate: (itemToEdit.serviceRate ?? itemToEdit.unit_price ?? itemToEdit.service_rate ?? 0).toString(),
            totalServiceAmount: (itemToEdit.totalServiceAmount ?? itemToEdit.total_amount ?? itemToEdit.total_service_amount ?? 0).toString(),
            serviceAddress: itemToEdit.serviceAddress || itemToEdit.shipping_address || "",
            shipping_address_id: itemToEdit.shipping_address_id || "",
            reportedComplaint: itemToEdit.reportedComplaint || itemToEdit.reported_complaints || itemToEdit.reported_complaint || "",
            category: itemToEdit.category || itemToEdit.category_name || "",
            category_name: itemToEdit.category_name || itemToEdit.category || "",
            sub_category: itemToEdit.sub_category || itemToEdit.sub_category_name || "",
            sub_category_name: itemToEdit.sub_category_name || itemToEdit.sub_category || "",
            make: itemToEdit.make || itemToEdit.make_name || "",
            make_name: itemToEdit.make_name || itemToEdit.make || "",
            unit: itemToEdit.unit || itemToEdit.unit_name || "",
            unit_name: itemToEdit.unit_name || itemToEdit.unit || "",
            item: itemToEdit.item || itemToEdit.product_id || "",
            item_name: itemToEdit.item_name || itemToEdit.serviceName || itemToEdit.product_name || "",
            sku: itemToEdit.sku || itemToEdit.product_sku || "",
            product_sku: itemToEdit.product_sku || itemToEdit.sku || "",
            hsn: itemToEdit.hsn || itemToEdit.hsn_code || "",
            hsn_code: itemToEdit.hsn_code || itemToEdit.hsn || "",
            discount: itemToEdit.discount || 0,
            discount_type: itemToEdit.discount_type || "%",
            discount_value: itemToEdit.discount_value || itemToEdit.discount || 0,
            gst_percent: itemToEdit.gst_percent || itemToEdit.gst_percentage || 0,
            gst_percentage: itemToEdit.gst_percentage || itemToEdit.gst_percent || 0,
            original_gst_percent: itemToEdit.original_gst_percent || itemToEdit.gst_percentage || 0,
            gst_value: itemToEdit.gst_value || 0,
            net_value: itemToEdit.net_value || 0,
            cgst_percent: itemToEdit.cgst_percent || 0,
            sgst_percent: itemToEdit.sgst_percent || 0,
            igst_percent: itemToEdit.igst_percent || 0,
            product_type: itemToEdit.product_type || [],
            prod_description: itemToEdit.prod_description || itemToEdit.description || "",
            additional_details: itemToEdit.additional_details || "",
        });

        setErrors({});
        const itemsEl = document.getElementById("items");
        if (itemsEl) {
            itemsEl.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleSubmit = async () => {
        try {
            await validationSchema.validate(formData, { abortEarly: false });
            setErrors({});

            const qty = parseFloat(formData.productQuantity) || 0;
            const rate = parseFloat(formData.serviceRate) || 0;

            // Calculate gross value based on currency
            // If currency is not INR, use rate_in_currency; otherwise use serviceRate
            let itemTotal = 0;
            if (currency_code && currency_code !== "INR") {
                // For foreign currency, use rate_in_currency
                const rateInCurrency = exchange_rate && Number(exchange_rate) > 0
                    ? (Number(formData.serviceRate || 0) / Number(exchange_rate))
                    : Number(formData.serviceRate || 0);
                itemTotal = qty * rateInCurrency;
            } else {
                // For INR, use serviceRate
                itemTotal = qty * rate;
            }

            // Discount calculations - use discount_value from form
            let discountValue = Number(formData.discount_value) || 0;
            let discountAmount = 0; // Calculated for display only, not sent to backend

            if (formData.discount_type === "%") {
                // Validate: If discount_type is "%", cap discount_value at 100%
                if (discountValue > 100) {
                    discountValue = 100;
                    toast.warning("Discount percentage cannot exceed 100%");
                    setFormData((prev) => ({ ...prev, discount_value: 100 }));
                    return; // Exit early to prevent submission with invalid discount
                }
                // Calculate discount_amount from percentage (for display only)
                discountAmount = itemTotal ? roundToTwoDecimals((itemTotal * discountValue) / 100) : 0;
            } else {
                // For currency discount, validate it doesn't exceed gross value
                if (discountValue > itemTotal) {
                    discountValue = itemTotal;
                    toast.warning("Discount amount cannot exceed gross value");
                    setFormData((prev) => ({ ...prev, discount_value: itemTotal }));
                    return; // Exit early to prevent submission with invalid discount
                }
                // For currency, discount_amount equals discount_value (for display)
                discountAmount = roundToTwoDecimals(discountValue);
            }

            const netValueAfterDiscount = itemTotal - discountValue;

            // GST calculations
            let gstValue = 0;
            if (!shouldZeroGstForCompany) {
                if (composition_gst && select_composition_gst) {
                    // Use composition GST
                    const compGstPercent = Number(select_composition_gst) || 0;
                    gstValue = roundToTwoDecimals((netValueAfterDiscount * compGstPercent) / 100);
                } else {
                    // Use standard GST - use original_gst_percent or gst_percentage
                    const gstPercentForCalc = Number(formData.original_gst_percent) || Number(formData.gst_percentage) || Number(formData.gst_percent) || 0;
                    gstValue = roundToTwoDecimals((netValueAfterDiscount * gstPercentForCalc) / 100);
                }
            }

            const netValue = netValueAfterDiscount;
            const finalTotal = netValue + gstValue;

            // Note: discountValue is the input value (sent to backend)
            // discountAmount is the calculated display value (not sent to backend)

            // Calculate rate_in_currency if exchange_rate is available
            const rateInCurrency = exchange_rate && Number(exchange_rate) > 0
                ? (Number(formData.serviceRate || 0) / Number(exchange_rate)).toFixed(2)
                : "";

            // Find shipping address object from shippingAddresses array
            let shippingAddressObj: any = null;

            // If serviceAddress is already an object, use it directly
            if (formData.serviceAddress && typeof formData.serviceAddress === 'object') {
                shippingAddressObj = {
                    shipping_address_id: formData.serviceAddress.shipping_address_id || formData.serviceAddress.id || "",
                    address: formData.serviceAddress.address || "",
                    city: formData.serviceAddress.city || "",
                    state: formData.serviceAddress.state || "",
                    country: formData.serviceAddress.country || "",
                    pin_code: formData.serviceAddress.pincode || formData.serviceAddress.pin_code || "",
                    phone_number: formData.serviceAddress.phone_number || "",
                };
            } else if (formData.shipping_address_id && shippingAddresses && shippingAddresses.length > 0) {
                // Fallback: find by ID
                const foundAddr = shippingAddresses.find(
                    (addr: any) => (addr.shipping_address_id || addr.id) === formData.shipping_address_id
                );
                if (foundAddr) {
                    shippingAddressObj = {
                        shipping_address_id: foundAddr.shipping_address_id || foundAddr.id || "",
                        address: foundAddr.address || "",
                        city: foundAddr.city || "",
                        state: foundAddr.state || "",
                        country: foundAddr.country || "",
                        pin_code: foundAddr.pincode || foundAddr.pin_code || "",
                        phone_number: foundAddr.phone_number || "",
                    };
                }
            }

            const payload: any = {
                ...formData,
                id: Date.now(),
                type: "service",
                quantity: formData.productQuantity,
                unit_price: formData.serviceRate,
                currency_code: currency_code || "INR",
                exchange_rate: exchange_rate || "1",
                sac_code: formData.sac_code || "",
                sac_code_id: formData.sac_code_id || "",
                sac: formData.sac_code_id || formData.sac_code || "", // For backward compatibility
                gross_value: (() => {
                    // Calculate gross value based on currency
                    // If currency is not INR, use rate_in_currency; otherwise use serviceRate
                    if (currency_code && currency_code !== "INR" && currency_code !== "IND") {
                        // For foreign currency, use rate_in_currency
                        const rateInCurrency = exchange_rate && Number(exchange_rate) > 0
                            ? (Number(formData.serviceRate || 0) / Number(exchange_rate))
                            : Number(formData.serviceRate || 0);
                        return (Number(formData.productQuantity || 0) * rateInCurrency).toFixed(2);
                    } else {
                        // For INR, use serviceRate
                        return (Number(formData.productQuantity || 0) * Number(formData.serviceRate || 0)).toFixed(2);
                    }
                })(),
                rate_in_currency: rateInCurrency,
                shipping_address_id: formData.shipping_address_id,
                shipping_address: shippingAddressObj, // Include full shipping_address object
                discount: formData.discount_value || 0, // Keep for backward compatibility
                discount_type: formData.discount_type,
                discount_percent: formData.discount_type === "%" ? discountValue : null, // Include discount_percent when discount_type is "%"
                discount_value: discountValue, // Send discount_value to backend
                discount_amount: discountAmount.toFixed(2), // Calculated value (display only, not sent to backend)
                total: itemTotal.toFixed(2),
                gross_total_amount: itemTotal.toFixed(2), // Gross value (for reference)
                total_amount: finalTotal.toFixed(2), // Final total (net + GST) - used for total_service_amount
                net_value: netValue.toFixed(2),
                gst_value: gstValue.toFixed(2),
                gst_percent: shouldZeroGstForCompany ? 0 : (Number(formData.original_gst_percent) || Number(formData.gst_percentage) || Number(formData.gst_percent) || 0),
                gst_percentage: Number(formData.gst_percentage) || Number(formData.gst_percent) || 0,
                original_gst_percent: Number(formData.original_gst_percent) || Number(formData.gst_percentage) || Number(formData.gst_percent) || 0,
                composition_gst: composition_gst ? composition_gst : "",
                select_composition_gst: select_composition_gst ? select_composition_gst : "",
            };

            if (!payload.item_name) {
                payload.item_name = formData.serviceName || formData.item_name || "";
            }
            payload.prod_description = formData.prod_description ?? "";
            payload.additional_details = formData.additional_details ?? "";

            // Check for duplicates before adding or updating
            const isDuplicateById = items.some(
                (existingItem, idx) => idx !== editingIndex && existingItem.item && existingItem.item === payload.item
            );

            const itemName = payload.serviceName || payload.item_name;
            const isDuplicateByName = items.some((existingItem, idx) => {
                if (idx === editingIndex) return false;
                const existingItemName = existingItem.serviceName || existingItem.item_name;
                return (
                    existingItemName &&
                    itemName &&
                    existingItemName.toLowerCase() === itemName.toLowerCase()
                );
            });

            if (isDuplicateById || isDuplicateByName) {
                toast.error(
                    "This item is already added. Please select a different item."
                );
                return;
            }

            if (editingIndex !== null) {
                const originalItem = items[editingIndex];
                const updatedPayload = {
                    ...originalItem,
                    ...payload,
                    id: originalItem.id || payload.id,
                    sales_order_service_item_id: originalItem.sales_order_service_item_id,
                    quotation_service_item_id: originalItem.quotation_service_item_id,
                };
                setItems((prev) => prev.map((item, idx) => (idx === editingIndex ? updatedPayload : item)));
                toast.success("Service updated successfully!");
                handleFormReset();
            } else {
                onAdd(payload);
                toast.success("Service added successfully!");
                handleFormReset();
            }
        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                const newErrors: { [key: string]: string } = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
                toast.error("Please fill all required fields correctly.");
            }
        }
    };

    const handleDelete = (index: number) => {
        const itemToDelete = items[index];
        if (itemToDelete && (itemToDelete.challan_status || itemToDelete.diff_status === "LOCKED")) {
            toast.warning("Service item is locked because a Challan has already been generated.");
            return;
        }
        if (editingIndex === index) {
            handleFormReset();
        } else if (editingIndex !== null && editingIndex > index) {
            setEditingIndex(editingIndex - 1);
        }
        const updated = [...items];
        updated.splice(index, 1);
        setItems(updated);
        // Clear SAC code error for deleted item
        setSacCodeErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            // Reindex remaining errors
            const reindexed: { [key: number]: string } = {};
            Object.keys(newErrors).forEach((key) => {
                const oldIndex = parseInt(key);
                if (oldIndex > index) {
                    reindexed[oldIndex - 1] = newErrors[oldIndex];
                } else if (oldIndex < index) {
                    reindexed[oldIndex] = newErrors[oldIndex];
                }
            });
            return reindexed;
        });
        // Clear Serial No. error for deleted item
        setSerialNoErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            // Reindex remaining errors
            const reindexed: { [key: number]: string } = {};
            Object.keys(newErrors).forEach((key) => {
                const oldIndex = parseInt(key);
                if (oldIndex > index) {
                    reindexed[oldIndex - 1] = newErrors[oldIndex];
                } else if (oldIndex < index) {
                    reindexed[oldIndex] = newErrors[oldIndex];
                }
            });
            return reindexed;
        });
        toast.success("Service deleted successfully!");
    };

    const handleOpenAddAccessories = (index: number) => {
        setAccessoriesServiceIndex(index);
        setShowAddAccessoriesModal(true);
    };

    const handleOpenViewAccessories = (index: number) => {
        setAccessoriesServiceIndex(index);
        setShowViewAccessoriesModal(true);
    };

    const handleSaveAccessories = (accessories: AccessoryItem[]) => {
        if (accessoriesServiceIndex === null) return;
        setItems((prev) =>
            prev.map((item, idx) =>
                idx === accessoriesServiceIndex
                    ? { ...item, accessories }
                    : item
            )
        );
        toast.success("Accessories saved successfully!");
    };

    // Validate SAC code for a specific item
    const validateSacCode = (index: number, item: any) => {
        // Don't validate if SAC code options haven't loaded yet
        if (!sacCodeOptions || sacCodeOptions.length === 0) {
            return true;
        }
        
        const hasSacCode = item?.sac_code && item.sac_code.trim() !== "";
        const hasSacCodeId = item?.sac_code_id && item.sac_code_id.trim() !== "";

        // If item has sac_code but no sac_code_id, try to find a match before showing error
        if (hasSacCode && !hasSacCodeId) {
            const matchedSacCode = sacCodeOptions.find((sac: any) => {
                return sac.sac_code === item.sac_code || 
                    sac.sac_code?.toString() === item.sac_code?.toString() ||
                    String(sac.sac_code) === String(item.sac_code);
            });
            // If we found a match, don't show error (matching will happen in useEffect)
            if (matchedSacCode) {
                setSacCodeErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[index];
                    return newErrors;
                });
                return true;
            } else {
                setSacCodeErrors((prev) => ({
                    ...prev,
                    [index]: "SAC Code is required"
                }));
                return false;
            }
        } else if (!hasSacCode) {
            // No sac_code at all - show error
            setSacCodeErrors((prev) => ({
                ...prev,
                [index]: "SAC Code is required"
            }));
            return false;
        } else {
            // Both sac_code and sac_code_id are present
            setSacCodeErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[index];
                return newErrors;
            });
            return true;
        }
    };

    // Validate all SAC codes
    const validateAllSacCodes = () => {
        // Don't validate if SAC code options haven't loaded yet (to avoid false positives)
        if (!sacCodeOptions || sacCodeOptions.length === 0) {
            return true;
        }
        
        const newErrors: { [key: number]: string } = {};
        items.forEach((item, index) => {
            // Check if item has sac_code (even without sac_code_id, we can try to match it)
            const hasSacCode = item?.sac_code && item.sac_code.trim() !== "";
            const hasSacCodeId = item?.sac_code_id && item.sac_code_id.trim() !== "";
            
            // If item has sac_code but no sac_code_id, try to find a match before showing error
            if (hasSacCode && !hasSacCodeId) {
                const matchedSacCode = sacCodeOptions.find((sac: any) => {
                    return sac.sac_code === item.sac_code || 
                        sac.sac_code?.toString() === item.sac_code?.toString() ||
                        String(sac.sac_code) === String(item.sac_code);
                });
                // If we found a match, don't show error (matching will happen in useEffect)
                if (!matchedSacCode) {
                    newErrors[index] = "SAC Code is required";
                }
            } else if (!hasSacCode) {
                // No sac_code at all - show error
                newErrors[index] = "SAC Code is required";
            }
        });
        setSacCodeErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate Serial No. for a specific item
    const validateSerialNo = (index: number, item: any) => {
        const hasSerialNo = item?.serialNo && item.serialNo.trim() !== "" && item.serialNo !== "-";

        if (!hasSerialNo) {
            setSerialNoErrors((prev) => ({
                ...prev,
                [index]: "Serial No. is required"
            }));
            return false;
        } else {
            setSerialNoErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[index];
                return newErrors;
            });
            return true;
        }
    };

    // Validate all Serial Nos.
    const validateAllSerialNos = () => {
        const newErrors: { [key: number]: string } = {};
        items.forEach((item, index) => {
            const hasSerialNo = item?.serialNo && item.serialNo.trim() !== "" && item.serialNo !== "-";
            if (!hasSerialNo) {
                newErrors[index] = "Serial No. is required";
            }
        });
        setSerialNoErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleItemUpdate = (index: number, fieldOrUpdates: string | any, value?: any) => {
        const updatedItems = [...items];
        let updatedItem = { ...updatedItems[index] };

        // Handle multi-field update if an object is passed
        if (typeof fieldOrUpdates === "object" && fieldOrUpdates !== null) {
            updatedItem = { ...updatedItem, ...fieldOrUpdates };
        } else {
            updatedItem[fieldOrUpdates] = value;
        }

        const field = typeof fieldOrUpdates === "string" ? fieldOrUpdates : "";
        const updates = typeof fieldOrUpdates === "object" ? fieldOrUpdates : { [fieldOrUpdates]: value };

        // If shipping_address_id is updated, also update the full shipping_address object
        if (updates.shipping_address_id && shippingAddresses && shippingAddresses.length > 0) {
            const foundAddr = shippingAddresses.find((addr: any) => (addr.shipping_address_id || addr.id) === updates.shipping_address_id);
            if (foundAddr) {
                updatedItem.shipping_address = {
                    shipping_address_id: foundAddr.id || "",
                    address: foundAddr.address || "",
                    city: foundAddr.city || "",
                    state: foundAddr.state || "",
                    country: foundAddr.country || "",
                    pin_code: foundAddr.pin_code || "",
                    phone_number: foundAddr.phone_number || "",
                };
            }
        }

        const needsRecalculation = Object.keys(updates).some(k =>
            ["productQuantity", "quantity", "serviceRate", "unit_price", "discount", "discount_value", "discount_type", "gst_percent", "gst_percentage", "sac_code_id", "sac_code"].includes(k)
        );

        if (needsRecalculation) {
            // If discount_type is being changed, reset discount_value to 0
            // But preserve GST-related fields to prevent them from being lost
            if (updates.discount_type) {
                const oldDiscountType = updatedItems[index].discount_type;
                const newDiscountType = updates.discount_type;
                if (oldDiscountType !== newDiscountType) {
                    updatedItem.discount_value = 0;
                    // Preserve GST-related fields from the original item
                    if (updatedItems[index].gst_percentage !== undefined) {
                        updatedItem.gst_percentage = updatedItems[index].gst_percentage;
                    }
                    if (updatedItems[index].original_gst_percent !== undefined) {
                        updatedItem.original_gst_percent = updatedItems[index].original_gst_percent;
                    }
                    if (updatedItems[index].gst_id !== undefined) {
                        updatedItem.gst_id = updatedItems[index].gst_id;
                    }
                    if (updatedItems[index].gst_percent !== undefined) {
                        updatedItem.gst_percent = updatedItems[index].gst_percent;
                    }
                }
            }

            const qty = parseFloat(updatedItem.productQuantity || updatedItem.quantity) || 0;
            const rate = parseFloat(updatedItem.serviceRate || updatedItem.unit_price) || 0;

            // Calculate gross value based on currency
            // If currency is not INR, use rate_in_currency; otherwise use serviceRate/unit_price
            let itemTotal = 0;
            let grossValue = 0;
            if (updatedItem.currency_code && updatedItem.currency_code !== "INR" && updatedItem.currency_code !== "IND") {
                // For foreign currency, use rate_in_currency
                const rateInCurrency = updatedItem.rate_in_currency
                    ? Number(updatedItem.rate_in_currency)
                    : (updatedItem.exchange_rate && Number(updatedItem.exchange_rate) > 0
                        ? (Number(updatedItem.serviceRate || updatedItem.unit_price || 0) / Number(updatedItem.exchange_rate))
                        : Number(updatedItem.serviceRate || updatedItem.unit_price || 0));
                itemTotal = roundToTwoDecimals(qty * rateInCurrency);
                grossValue = itemTotal;
            } else {
                // For INR, use serviceRate/unit_price
                itemTotal = roundToTwoDecimals(qty * rate);
                grossValue = itemTotal;
            }

            // Gross value calculation with proper rounding
            updatedItem.gross_value = grossValue.toFixed(2);
            // Calculate rate_in_currency if exchange_rate is available
            if (updatedItem.exchange_rate && Number(updatedItem.exchange_rate) > 0) {
                updatedItem.rate_in_currency = (Number(updatedItem.serviceRate || updatedItem.unit_price || 0) / Number(updatedItem.exchange_rate)).toFixed(2);
            } else if (updatedItem.rate_in) {
                updatedItem.rate_in_currency = Number(updatedItem.rate_in).toFixed(2);
            }

            // ---------------------------
            // 2️⃣ DISCOUNT CALCULATION
            // ---------------------------
            let discountValue = Number(updatedItem.discount_value) || 0;
            let discountAmount = 0; // Calculated for display only, not sent to backend

            // Handle field updates
            // Handle field updates
            if (updates.discount_value !== undefined) {
                // User is directly editing the discount_value field
                discountValue = Number(updates.discount_value) || 0;
            } else if (updates.discount !== undefined) {
                // Backward compatibility: if discount field is used, treat it as discount_value
                discountValue = Number(updates.discount) || 0;
            }

            // Calculate discount_amount based on discount_type
            const discountType = updates.discount_type || updatedItem.discount_type || "%";

            // Check if discount_type is percentage (could be "%" or "percent" or "percentage")
            const isPercentageDiscount = discountType === "%" ||
                discountType === "percent" ||
                discountType === "percentage" ||
                discountType?.toLowerCase() === "percent" ||
                discountType?.toLowerCase() === "percentage";

            if (isPercentageDiscount) {
                // Validate: cap discount_value at 100%
                if (discountValue > 100) {
                    discountValue = 100;
                    toast.warning("Discount percentage cannot exceed 100%");
                    updatedItem.discount_value = 100;
                }
                // Calculate discount_amount from percentage (for display only)
                discountAmount = itemTotal
                    ? roundToTwoDecimals((itemTotal * discountValue) / 100)
                    : 0;
                // Store percentage in discount_percent
                updatedItem.discount_percent = discountValue;
            } else {
                // For fixed amount (currency), discount_value IS the discount_amount
                // Validate: discount_value cannot exceed gross value (itemTotal)
                if (discountValue > itemTotal) {
                    discountValue = itemTotal;
                    toast.warning("Discount amount cannot exceed gross value");
                    updatedItem.discount_value = itemTotal;
                }
                // For currency, discount_amount equals discount_value (for display)
                discountAmount = roundToTwoDecimals(discountValue);
                // Clear discount_percent when using currency
                updatedItem.discount_percent = null;
            }

            // Store discount_value (sent to backend) and discount_amount (display only)
            updatedItem.discount_value = discountValue;
            updatedItem.discount_amount = discountAmount;
            updatedItem.discount = discountValue; // Keep for backward compatibility

            const netValueAfterDiscount = roundToTwoDecimals(itemTotal - discountAmount);

            // GST calculations
            let gstValue = 0;
            // When gst_percent or gst_percentage is being updated, use the new value directly
            let gstPercentForCalc = 0;
            if (updates.gst_percent !== undefined || updates.gst_percentage !== undefined) {
                const newVal = updates.gst_percent !== undefined ? updates.gst_percent : updates.gst_percentage;
                // console.log("gst_percent or gst_percentage => in servicesection", newVal)
                // Force the new value into the item so the math uses it immediately
                gstPercentForCalc = shouldZeroGstForCompany ? 0 : Number(newVal) || 0;
                updatedItem.original_gst_percent = Number(newVal) || 0;
                updatedItem.gst_percentage = Number(newVal) || 0;
                updatedItem.gst_percent = gstPercentForCalc; // Ensure this is set
            } else {
                gstPercentForCalc = shouldZeroGstForCompany ? 0 : Number(updatedItem.original_gst_percent) || Number(updatedItem.gst_percentage) || 0;
            }

            if (!shouldZeroGstForCompany) {
                if (updatedItem.composition_gst && updatedItem.select_composition_gst) {
                    // Use composition GST
                    // console.log("using composition gst => in servicesection", updatedItem.select_composition_gst)
                    const compGstPercent = Number(updatedItem.select_composition_gst) || 0;
                    gstValue = roundToTwoDecimals((netValueAfterDiscount * compGstPercent) / 100);
                } else {
                    // Use standard GST
                    gstValue = roundToTwoDecimals((netValueAfterDiscount * gstPercentForCalc) / 100);
                }
            }

            // Update gst_percent to reflect the effective GST percentage (0 for compositional companies)
            const displayGstPercent = shouldZeroGstForCompany ? 0 : gstPercentForCalc;

            const netValue = roundToTwoDecimals(netValueAfterDiscount);
            const finalTotal = roundToTwoDecimals(netValue + gstValue);

            updatedItem.discount_type = discountType;

            updatedItem.totalServiceAmount = finalTotal.toFixed(2); // Final total (net + GST)
            updatedItem.total = itemTotal.toFixed(2); // Gross value (for reference)
            updatedItem.total_amount = finalTotal.toFixed(2); // Final total (net + GST) - used for total_service_amount
            updatedItem.total_price = finalTotal.toFixed(2); // Final total (net + GST) - used for total_service_amount
            updatedItem.net_value = netValue.toFixed(2);
            updatedItem.gst_value = roundToTwoDecimals(gstValue).toFixed(2);
            updatedItem.gst_percent = displayGstPercent;
            // Preserve gst_percentage and original_gst_percent if they exist (important when discount_type changes)
            if (updatedItem.gst_percentage === undefined && updatedItems[index].gst_percentage !== undefined) {
                updatedItem.gst_percentage = updatedItems[index].gst_percentage;
            }
            if (updatedItem.original_gst_percent === undefined && updatedItems[index].original_gst_percent !== undefined) {
                updatedItem.original_gst_percent = updatedItems[index].original_gst_percent;
            }
            // console.log("billFromState => in servicesection", billFromState)
            // console.log("billToState => in servicesection", billToState)
            // Preserve currency_code and exchange_rate if not already set
            if (!updatedItem.currency_code) {
                updatedItem.currency_code = currency_code || "INR";
            }
            if (!updatedItem.exchange_rate) {
                updatedItem.exchange_rate = exchange_rate || "1";
            }

            // Recalculate GST split
            if (shouldZeroGstForCompany) {
                updatedItem.cgst_percent = 0;
                updatedItem.sgst_percent = 0;
                updatedItem.igst_percent = 0;
            } else if (!billFromState || !billToState) {
                updatedItem.cgst_percent = 0;
                updatedItem.sgst_percent = 0;
                updatedItem.igst_percent = gstPercentForCalc;
                // console.log("inter-state => in servicesection", gstPercentForCalc)
            } else if (billFromState === billToState) {
                // console.log("intra-state => in servicesection", gstPercentForCalc)
                updatedItem.cgst_percent = gstPercentForCalc / 2;
                updatedItem.sgst_percent = gstPercentForCalc / 2;
                updatedItem.igst_percent = 0;
            } else {
                updatedItem.cgst_percent = 0;
                updatedItem.sgst_percent = 0;
                updatedItem.igst_percent = gstPercentForCalc;
            }
        }
        // console.log("updatedItem => in servicesection", updatedItem)
        updatedItems[index] = updatedItem;
        setItems(updatedItems);

        // Validate SAC code if it was updated
        if (updates.sac_code !== undefined || updates.sac_code_id !== undefined) {
            validateSacCode(index, updatedItem);
        }

        // Validate Serial No. if it was updated
        if (updates.serialNo !== undefined) {
            validateSerialNo(index, updatedItem);
        }
    };

    // Helper function to get effective company ID from various sources
    const getEffectiveCompanyId = (): string | null => {
        // Priority 1: Direct companyId prop (check for truthy and non-empty string)
        if (companyId && companyId !== "" && companyId !== "null" && companyId !== "undefined") {
            return companyId;
        }

        // Priority 2: From company object
        if (company?.company_id && company.company_id !== "" && company.company_id !== "null") {
            return company.company_id;
        }
        if (company?.id && company.id !== "" && company.id !== "null") {
            return company.id;
        }

        // Priority 3: From shippingAddresses (check company_id, NOT id which is shipping address ID)
        if (shippingAddresses && Array.isArray(shippingAddresses) && shippingAddresses.length > 0) {
            const firstAddr = shippingAddresses[0];
            if (firstAddr?.company_id && firstAddr.company_id !== "" && firstAddr.company_id !== "null") {
                return firstAddr.company_id;
            }
        }

        return null;
    };

    const handleAddNewAddress = () => {
        // Always open modal - it will show "Customer Selection Required" message if no company ID
        setShowShippingAddressModal(true);
    };

    return (
        <>
            <h5 className="mb-3" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                Services
            </h5>
            <div
                className="p-3 rounded mb-4"
                style={{
                    background: MecordColors.ActiveBackground,
                }}
            >

                <Form onSubmit={(e) => e.preventDefault()}>
                    {/* Row 1: Model, Service, Serial No */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-3">
                            <Form.Group>
                                <Form.Label style={errors.model ? { color: "red" } : {}}>
                                    Model <span className="text-danger">*</span>
                                </Form.Label>
                                <Autocomplete
                                    options={modelOptions}
                                    getOptionLabel={(option: any) => option?.model_name || ""}
                                    value={selectedModel}
                                    onChange={handleModelSelect}
                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                    renderOption={(props, option) =>
                                        autocompleteDropdownStyles.renderOption(
                                            props,
                                            option,
                                            (opt) => opt?.model_name || ""
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Model"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.model}
                                            helperText={errors.model}
                                            sx={{
                                                ...autocompleteDropdownStyles.inputSx,
                                                                        ...getDisabledInputStyles(false),
                                            }}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-3">
                            <Form.Group>
                                <Form.Label style={errors.serviceName ? { color: "red" } : {}}>
                                    Product <span className="text-danger">*</span>
                                </Form.Label>
                                <Autocomplete
                                    options={serviceOptions}
                                    getOptionLabel={(option: any) => option?.product_name || ""}
                                    value={selectedService}
                                    onChange={handleServiceSelect}
                                    disabled={!selectedModel}
                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                    renderOption={(props, option) =>
                                        autocompleteDropdownStyles.renderOption(
                                            props,
                                            option,
                                            (opt) => opt?.product_name || ""
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Service"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.serviceName}
                                            helperText={errors.serviceName}
                                            sx={{
                                                ...autocompleteDropdownStyles.inputSx,
                                                ...getDisabledInputStyles(!selectedModel),
                                            }}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-3">
                            <Form.Group>
                                <Form.Label style={errors.serialNo ? { color: "red" } : {}}>
                                    Serial No. <span className="text-danger">*</span>
                                </Form.Label>
                                <div style={{ position: "relative" }}>
                                    <Form.Control
                                        type="text"
                                        name="serialNo"
                                        value={formData.serialNo}
                                        onChange={handleInputChange}
                                        placeholder="Enter Serial No."
                                        style={{
                                            ...commonInputStyle,
                                            paddingRight: formData.serialNo && formData.serialNo.trim() !== "" ? "40px" : undefined,
                                        }}
                                        className="form-control-placeholder"
                                        isInvalid={!!errors.serialNo}
                                    />
                                    {formData.serialNo && formData.serialNo.trim() !== "" && (
                                        <InfoIcon
                                            style={{
                                                cursor: "pointer",
                                                color: MecordColors?.ThemeBGColor || "#007bff",
                                                fontSize: "20px",
                                                position: "absolute",
                                                right: "10px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                zIndex: 10,
                                            }}
                                            onClick={() => fetchProductHistory(formData.serialNo)}
                                        />
                                    )}
                                </div>
                                {errors.serialNo && <small className="text-danger">{errors.serialNo}</small>}
                            </Form.Group>
                        </div>

                        {/* SAC Code Field - Always visible after Execution Mode */}
                        <div className="col-12 col-md-6 col-lg-3">
                            <Form.Group>
                                <Form.Label style={errors.sac_code || errors.sac_code_id ? { color: "red" } : {}}>
                                    SAC Code <span className="text-danger">*</span>
                                </Form.Label>
                                <Autocomplete
                                    options={sacCodeOptions}
                                    getOptionLabel={(option: any) =>
                                        typeof option === "string"
                                            ? option
                                            : option.sac_code || ""
                                    }
                                    value={selectedSacCode || null}
                                    onChange={(event, newValue: any) => {
                                        setSelectedSacCode(newValue);

                                        if (newValue) {
                                            // Set SAC code fields
                                            setFormData((prev) => ({
                                                ...prev,
                                                sac_code: newValue.sac_code || "",
                                                sac_code_id: newValue.sac_code_id || newValue.id || "",
                                            }));

                                            // Clear errors when SAC code is selected
                                            if (errors.sac_code) {
                                                setErrors((prev) => ({ ...prev, sac_code: "" }));
                                            }
                                            if (errors.sac_code_id) {
                                                setErrors((prev) => ({ ...prev, sac_code_id: "" }));
                                            }

                                            // Map GST from SAC code
                                            const gstFromSac = newValue.gst?.gst_percentage ||
                                                newValue.gst_percentage ||
                                                (newValue.gst_id && gstOptions.find((g: any) => g.gst_id === newValue.gst_id)?.gst_percentage) ||
                                                0;

                                            if (gstFromSac > 0) {
                                                const gstOption = gstOptions.find((g: any) =>
                                                    Number(g.gst_percentage) === Number(gstFromSac)
                                                ) || gstOptions.find((g: any) => g.gst_id === newValue.gst_id);

                                                if (gstOption) {
                                                    setSelectedGst(gstOption);
                                                    const gstPercent = shouldZeroGstForCompany ? 0 : Number(gstFromSac);

                                                    // Calculate split for formData
                                                    let cgst = 0, sgst = 0, igst = 0;
                                                    if (!shouldZeroGstForCompany) {
                                                        if (!billFromState || !billToState) {
                                                            igst = Number(gstFromSac);
                                                        } else if (billFromState === billToState) {
                                                            cgst = Number(gstFromSac) / 2;
                                                            sgst = Number(gstFromSac) / 2;
                                                        } else {
                                                            igst = Number(gstFromSac);
                                                        }
                                                    }

                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        gst_percent: gstPercent,
                                                        gst_percentage: Number(gstFromSac),
                                                        original_gst_percent: Number(gstFromSac),
                                                        cgst_percent: cgst,
                                                        sgst_percent: sgst,
                                                        igst_percent: igst,
                                                    }));
                                                } else {
                                                    // If GST option not found, still set the percentage
                                                    const gstPercent = shouldZeroGstForCompany ? 0 : Number(gstFromSac);

                                                    // Calculate split for formData
                                                    let cgst = 0, sgst = 0, igst = 0;
                                                    if (!shouldZeroGstForCompany) {
                                                        if (!billFromState || !billToState) {
                                                            igst = Number(gstFromSac);
                                                        } else if (billFromState === billToState) {
                                                            cgst = Number(gstFromSac) / 2;
                                                            sgst = Number(gstFromSac) / 2;
                                                        } else {
                                                            igst = Number(gstFromSac);
                                                        }
                                                    }

                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        gst_percent: gstPercent,
                                                        gst_percentage: Number(gstFromSac),
                                                        original_gst_percent: Number(gstFromSac),
                                                        cgst_percent: cgst,
                                                        sgst_percent: sgst,
                                                        igst_percent: igst,
                                                    }));
                                                    setSelectedGst(null);
                                                }
                                            }
                                        } else {
                                            setFormData((prev) => ({
                                                ...prev,
                                                sac_code: "",
                                                sac_code_id: "",
                                            }));
                                            setSelectedGst(null);
                                        }
                                    }}
                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                    renderOption={(props, option) =>
                                        autocompleteDropdownStyles.renderOption(
                                            props,
                                            option,
                                            (opt) => typeof opt === "string" ? opt : (opt.sac_code || "")
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select SAC Code"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.sac_code || !!errors.sac_code_id}
                                            helperText={errors.sac_code || errors.sac_code_id || ""}
                                            required
                                            sx={{
                                                ...autocompleteDropdownStyles.inputSx,
                                                ...getDisabledInputStyles(false),
                                            }}
                                        />
                                    )}
                                />
                                {(errors.sac_code || errors.sac_code_id) && (
                                    <small className="text-danger" style={{ fontSize: "12px", display: "block", marginTop: "4px" }}>
                                        {errors.sac_code || errors.sac_code_id}
                                    </small>
                                )}
                            </Form.Group>
                        </div>
                    </div>

                    {/* Row 2: Service Category, Method, Type, Execution Mode */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6 col-lg-3">
                            <Form.Group>
                                <Form.Label style={errors.serviceCategory ? { color: "red" } : {}}>
                                    Service Category <span className="text-danger">*</span>
                                </Form.Label>
                                <Autocomplete
                                    options={serviceCategoryOptions}
                                    getOptionLabel={(option: any) => option.name || ""}
                                    value={
                                        serviceCategoryOptions.find((s) => s.name === formData.serviceCategory) || null
                                    }
                                    onChange={(event, newValue: any) => {
                                        const newCategory = newValue?.name || "";

                                        // Reset fields based on new category to avoid invalid states
                                        let newMethod = "";
                                        let newType = "";
                                        let newMode = "";

                                        // Logic to pre-set or clear fields based on category
                                        if (newCategory === "Installation") {
                                            newMethod = "Onsite"; // Force Onsite
                                            newType = ""; // Hide Type
                                            // Mode stays empty or user selects
                                        } else if (newCategory === "Repair") {
                                            newMethod = ""; // Hide Method
                                            newType = ""; // Hide Type
                                            // Don't auto-fill mode - user selects
                                        } else if (newCategory === "RMA" || newCategory === "Testing") {
                                            // Hide all
                                            newMethod = "NA";
                                            newType = "NA";
                                            newMode = "NA";
                                        } else if (newCategory === "Others") {
                                            newType = ""; // Hide Type
                                        }

                                        // If we are hiding them, maybe we should set them to a default "N/A" or empty string if validation allows.
                                        // Based on validation schema, they are required. So we might need to adjust validation or auto-fill "N/A".
                                        // For now, I will clear them. If they are hidden, they shouldn't trigger validation error?
                                        // *Correction*: If hidden, I must ensure validation doesn't block.
                                        // But since I can't easily change the hook schema dynamically without re-render,
                                        // I will set them to "N/A" or a dummy value if hidden, OR update validation schema.
                                        // Simple approach: Set to "Standard" / "Offsite" / "Inhouse" etc if hidden, or a specific "N/A" value.
                                        // Let's check the options.

                                        setFormData((prev) => ({
                                            ...prev,
                                            serviceCategory: newCategory,
                                            serviceMethod: newMethod,
                                            serviceType: newType,
                                            executionMode: newMode
                                        }));

                                        if (errors.serviceCategory) {
                                            setErrors((prev) => ({ ...prev, serviceCategory: "" }));
                                        }
                                    }}
                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                    renderOption={(props, option) =>
                                        autocompleteDropdownStyles.renderOption(props, option, (opt) => opt.name)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Service Category"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.serviceCategory}
                                            helperText={errors.serviceCategory}
                                            sx={{
                                                ...autocompleteDropdownStyles.inputSx,
                                                ...getDisabledInputStyles(false),
                                            }}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </div>

                        {/* Logic for Visibility */}
                        {(() => {
                            const { serviceCategory, serviceType } = formData;

                            const isRmaOrTesting = serviceCategory === "RMA" || serviceCategory === "Testing";
                            const isRepair = serviceCategory === "Repair";
                            const isInstallation = serviceCategory === "Installation";
                            const isOther = serviceCategory === "Others";
                            const isCalibrationOrRepair = serviceCategory === "Calibration" || serviceCategory === "Calibration & Repair";

                            // Visibility Rules
                            // 1. Calibration: Method (Offsite/Onsite) → Type (Standard/NABL) → Mode (Inhouse or 3rd party)
                            // 2. Repair: Mode (Inhouse or 3rd party) - no Method, no Type
                            // 3. Calibration & Repair: Same as Calibration
                            // 4. Installation: Method (Onsite) → Mode (Inhouse or 3rd party) - no Type
                            // 5. Others: Method (Offsite/Onsite) → Mode (Inhouse or 3rd party) - no Type
                            // 6. Testing: Hide all
                            // 7. RMA: Hide all

                            const showMethod = !isRmaOrTesting && !isRepair;
                            const showType = !isRmaOrTesting && !isRepair && !isInstallation && !isOther;

                            // Show mode for all except RMA/Testing
                            const showMode = !isRmaOrTesting;

                            // Filter Options
                            let currentMethodOptions = serviceMethodOptions;
                            if (isInstallation) {
                                currentMethodOptions = serviceMethodOptions.filter(o => o.name === "Onsite");
                            }

                            // All categories show both Inhouse and 3rd party options
                            let currentModeOptions = executionModeOptions;

                            return (
                                <>
                                    {showMethod && (
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <Form.Group>
                                                <Form.Label style={errors.serviceMethod ? { color: "red" } : {}}>
                                                    Service Method <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Autocomplete
                                                    options={currentMethodOptions}
                                                    getOptionLabel={(option: any) => option.name || ""}
                                                    value={serviceMethodOptions.find((s) => s.name === formData.serviceMethod) || null}
                                                    onChange={(event, newValue: any) => {
                                                        setFormData((prev) => ({ ...prev, serviceMethod: newValue?.name || "" }));
                                                        if (errors.serviceMethod) {
                                                            setErrors((prev) => ({ ...prev, serviceMethod: "" }));
                                                        }
                                                    }}
                                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                                    renderOption={(props, option) =>
                                                        autocompleteDropdownStyles.renderOption(props, option, (opt) => opt.name)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select Method"
                                                            variant="outlined"
                                                            size="small"
                                                            error={!!errors.serviceMethod}
                                                            helperText={errors.serviceMethod}
                                                            sx={{
                                                                ...autocompleteDropdownStyles.inputSx,
                                                                ...getDisabledInputStyles(false),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Form.Group>
                                        </div>
                                    )}

                                    {showType && (
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <Form.Group>
                                                <Form.Label style={errors.serviceType ? { color: "red" } : {}}>
                                                    Service Type <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Autocomplete
                                                    options={serviceTypeOptions}
                                                    getOptionLabel={(option: any) => option.name || ""}
                                                    value={serviceTypeOptions.find((s) => s.name === formData.serviceType) || null}
                                                    onChange={(event, newValue: any) => {
                                                        const newType = newValue?.name || "";

                                                        // Don't clear mode when changing type - keep current selection
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            serviceType: newType,
                                                            // Keep execution mode as is
                                                        }));

                                                        if (errors.serviceType) {
                                                            setErrors((prev) => ({ ...prev, serviceType: "" }));
                                                        }
                                                    }}
                                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                                    renderOption={(props, option) =>
                                                        autocompleteDropdownStyles.renderOption(props, option, (opt) => opt.name)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select Type"
                                                            variant="outlined"
                                                            size="small"
                                                            error={!!errors.serviceType}
                                                            helperText={errors.serviceType}
                                                            sx={{
                                                                ...autocompleteDropdownStyles.inputSx,
                                                                ...getDisabledInputStyles(false),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Form.Group>
                                        </div>
                                    )}

                                    {showMode && (
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <Form.Group>
                                                <Form.Label style={errors.executionMode ? { color: "red" } : {}}>
                                                    Execution Mode <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Autocomplete
                                                    options={currentModeOptions}
                                                    getOptionLabel={(option: any) => option.name || ""}
                                                    value={executionModeOptions.find((s) => s.name === formData.executionMode) || null}
                                                    onChange={(event, newValue: any) => {
                                                        setFormData((prev) => ({ ...prev, executionMode: newValue?.name || "" }));
                                                        if (errors.executionMode) {
                                                            setErrors((prev) => ({ ...prev, executionMode: "" }));
                                                        }
                                                    }}
                                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                                    renderOption={(props, option) =>
                                                        autocompleteDropdownStyles.renderOption(props, option, (opt) => opt.name)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select Mode"
                                                            variant="outlined"
                                                            size="small"
                                                            error={!!errors.executionMode}
                                                            helperText={errors.executionMode}
                                                            sx={{
                                                                ...autocompleteDropdownStyles.inputSx,
                                                                ...getDisabledInputStyles(false),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Form.Group>
                                        </div>
                                    )}

                                </>
                            );
                        })()}
                    </div>

                    {/* Row 3: Product Quantity, Service Rate, Total Service Amount, Service Address */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6 col-lg-3">
                            <Form.Group>
                                <Form.Label style={errors.productQuantity ? { color: "red" } : {}}>
                                    Product Quantity <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="productQuantity"
                                    value={formData.productQuantity}
                                    onChange={handleInputChange}
                                    placeholder="Default (1)"
                                    style={commonInputStyle}
                                    className="form-control-placeholder"
                                    isInvalid={!!errors.productQuantity}
                                    disabled={true}
                                />
                                {errors.productQuantity && (
                                    <small className="text-danger">{errors.productQuantity}</small>
                                )}
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                            <Form.Group>
                                <Form.Label style={errors.serviceRate ? { color: "red" } : {}}>
                                    Service Rate <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="serviceRate"
                                    value={formData.serviceRate}
                                    onChange={handleInputChange}
                                    placeholder="Enter Rate"
                                    style={commonInputStyle}
                                    className="form-control-placeholder"
                                    isInvalid={!!errors.serviceRate}
                                />
                                {errors.serviceRate && <small className="text-danger">{errors.serviceRate}</small>}
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                            <Form.Group>
                                <Form.Label>
                                    Total Amount <span className="text-secondary">(Auto)</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="totalServiceAmount"
                                    value={formData.totalServiceAmount}
                                    readOnly
                                    placeholder="0.00"
                                    style={{ ...commonInputStyle, backgroundColor: "#e9ecef" }}
                                    className="form-control-placeholder"
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                            <Form.Group>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="mb-0" style={errors.serviceAddress ? { color: "red" } : {}}>
                                        Service Address <span className="text-danger">*</span>
                                    </Form.Label>
                                    <span
                                        style={{
                                            color: MecordColors.ThemeBGColor,
                                            fontSize: "0.85rem",
                                            cursor: "pointer",
                                            fontWeight: "500"
                                        }}
                                        onClick={handleAddNewAddress}
                                    >
                                        <i className="bi bi-plus-circle" style={{ marginRight: "4px" }}></i>
                                        Add New
                                    </span>
                                </div>
                                <Autocomplete
                                    options={serviceAddressOptions}
                                    getOptionLabel={(option: any) => option.name || ""}
                                    isOptionEqualToValue={(option: any, value: any) =>
                                        option.id === value?.id ||
                                        option.shipping_address_id === value?.shipping_address_id
                                    }
                                    value={
                                        (() => {
                                            // If serviceAddress is an object, find matching option
                                            if (formData.serviceAddress && typeof formData.serviceAddress === 'object') {
                                                const addressId = formData.serviceAddress.shipping_address_id || formData.serviceAddress.id;
                                                const found = serviceAddressOptions.find(
                                                    (s) => s.shipping_address_id === addressId || s.id === addressId
                                                );
                                                if (found) return found;

                                                // If not found in options, create a temporary option from the object
                                                const addr = formData.serviceAddress;
                                                return {
                                                    id: addr.shipping_address_id || addr.id,
                                                    shipping_address_id: addr.shipping_address_id || addr.id,
                                                    name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode || addr.pin_code}`,
                                                    address: addr.address,
                                                    city: addr.city,
                                                    state: addr.state,
                                                    country: addr.country,
                                                    pincode: addr.pincode || addr.pin_code,
                                                    phone_number: addr.phone_number,
                                                    phone_country: addr.phone_country,
                                                    company_id: addr.company_id,
                                                    is_default: addr.is_default,
                                                    created_at: addr.created_at,
                                                    updated_at: addr.updated_at,
                                                    fullAddress: addr
                                                };
                                            }

                                            // Legacy support: if serviceAddress is a string, try to match
                                            if (typeof formData.serviceAddress === 'string' && formData.serviceAddress) {
                                                const found = serviceAddressOptions.find(
                                                    (s) => s.name === formData.serviceAddress
                                                );
                                                if (found) return found;
                                            }

                                            return null;
                                        })()
                                    }
                                    onChange={(event, newValue: any) => {
                                        if (newValue) {
                                            // Get the full address object from shippingAddresses array
                                            const addressId = newValue.shipping_address_id || newValue.id;

                                            // First try to get from shippingAddresses (most reliable)
                                            let fullAddress = shippingAddresses?.find(
                                                (addr: any) => (addr.shipping_address_id || addr.id) === addressId
                                            );

                                            // If not found, try fullAddress from the option
                                            if (!fullAddress && newValue.fullAddress) {
                                                fullAddress = newValue.fullAddress;
                                            }

                                            // If still not found, construct from newValue fields
                                            if (!fullAddress) {
                                                fullAddress = {
                                                    shipping_address_id: addressId,
                                                    company_id: newValue.company_id,
                                                    address: newValue.address || "",
                                                    city: newValue.city || "",
                                                    state: newValue.state || "",
                                                    country: newValue.country || "India",
                                                    pincode: newValue.pincode || "",
                                                    pin_code: newValue.pincode || "",
                                                    phone_number: newValue.phone_number || "",
                                                    phone_country: newValue.phone_country || "+91",
                                                    is_default: newValue.is_default || false,
                                                    created_at: newValue.created_at,
                                                    updated_at: newValue.updated_at
                                                };
                                            }

                                            // Ensure all required fields are present
                                            if (!fullAddress.shipping_address_id && !fullAddress.id) {
                                                console.warn("Incomplete shipping address object:", fullAddress);
                                            }

                                            setFormData((prev) => ({
                                                ...prev,
                                                serviceAddress: fullAddress,
                                                shipping_address_id: addressId,
                                                shipping_address: fullAddress, // Include full shipping_address object
                                            }));
                                        } else {
                                            setFormData((prev) => ({
                                                ...prev,
                                                serviceAddress: null,
                                                shipping_address_id: "",
                                                shipping_address: null,
                                            }));
                                        }
                                        if (errors.serviceAddress) {
                                            setErrors((prev) => ({ ...prev, serviceAddress: "" }));
                                        }
                                    }}
                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                    renderOption={(props, option) =>
                                        autocompleteDropdownStyles.renderOption(
                                            props,
                                            option,
                                            (opt) => opt.name
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Address"
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.serviceAddress}
                                            helperText={errors.serviceAddress}
                                            sx={{
                                                ...autocompleteDropdownStyles.inputSx,
                                                ...getDisabledInputStyles(false),
                                            }}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    {/* Row 4: Reported Complaint + Add Button */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-10">
                            <Form.Group>
                                <Form.Label>Reported Complaint</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="reportedComplaint"
                                    value={formData.reportedComplaint}
                                    onChange={handleInputChange}
                                    placeholder="Enter Complaint"
                                    rows={1}
                                    style={commonInputStyle}
                                    className="form-control-placeholder"
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-2 d-flex justify-content-end align-items-end gap-2">
                            {editingIndex !== null && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleCancelEdit}
                                    style={{
                                        borderRadius: "8px",
                                        height: "44px",
                                    }}
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                className="d-flex align-items-center justify-content-center gap-2"
                                style={{
                                    backgroundColor: MecordColors.PrimaryButtonBGColor,
                                    borderColor: MecordColors.PrimaryButtonBGColor,
                                    borderRadius: "8px",
                                    height: "44px",
                                    width: editingIndex !== null ? "110px" : "100px",
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        MecordColors.PrimaryButtonHoverBGColor;
                                    e.currentTarget.style.borderColor =
                                        MecordColors.PrimaryButtonHoverBGColor;
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        MecordColors.PrimaryButtonBGColor;
                                    e.currentTarget.style.borderColor =
                                        MecordColors.PrimaryButtonBGColor;
                                }}
                            >
                                {editingIndex !== null ? (
                                    <>
                                        <EditIcon fontSize="small" />
                                        Update
                                    </>
                                ) : (
                                    <>
                                        <Add fontSize="small" />
                                        Add
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Form>

                {/* Table */}
                {Array.isArray(items) && items.length > 0 ? (
                    <div
                        className="table-responsive bg-white rounded"
                        style={{ overflowX: "auto", maxWidth: "100%" }}
                    >
                        <Table
                            hover
                            style={{
                                minWidth: "2550px",
                                tableLayout: "fixed",
                                width: "2550px",
                            }}
                        >
                            <thead>
                                <tr
                                    style={{ backgroundColor: MecordColors.LightGrayColor }}
                                >
                                    <th style={{ width: "60px", whiteSpace: "nowrap" }}>Sr No.</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Model</th>
                                    <th style={{ width: "200px", whiteSpace: "nowrap" }}>Product</th>
                                    <th style={{ width: "150px", whiteSpace: "nowrap" }}>Additional Details</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Serial No. <span className="text-danger">*</span></th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>SAC Code <span className="text-danger">*</span></th>
                                    <th style={{ width: "150px", whiteSpace: "nowrap" }}>Service Category</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Service Method</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Service Type</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Execution Mode</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Product Quantity</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Service Rate</th>
                                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>Gross Value</th>
                                    <th style={{ width: "180px", whiteSpace: "nowrap" }}>Discount/Flat</th>
                                    <th style={{ width: "110px", whiteSpace: "nowrap" }}>Discount Value</th>
                                    <th style={{ width: "100px", whiteSpace: "nowrap" }}>Net Value</th>
                                    <th style={{ width: "80px", whiteSpace: "nowrap" }}>GST (%)</th>
                                    {isIntraState ? (
                                        <>
                                            <th style={{ width: "100px", whiteSpace: "nowrap" }}>SGST (%)</th>
                                            <th style={{ width: "100px", whiteSpace: "nowrap" }}>CGST (%)</th>
                                        </>
                                    ) : (
                                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>IGST (%)</th>
                                    )}
                                    <th style={{ width: "100px", whiteSpace: "nowrap" }}>GST Value</th>
                                    <th style={{ width: "160px", whiteSpace: "nowrap" }}>Total Service Amount</th>
                                    <th style={{ width: "180px", whiteSpace: "nowrap" }}>Total Accessories Amount</th>
                                    <th style={{ width: "250px", whiteSpace: "nowrap" }}>Service Address</th>
                                    <th style={{ width: "200px", whiteSpace: "nowrap" }}>Reported Complaint</th>
                                    <th
                                        style={{
                                            position: "sticky",
                                            right: 0,
                                            backgroundColor: MecordColors.LightGrayColor,
                                            borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                                            zIndex: 10,
                                            width: "80px",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody style={{ backgroundColor: "transparent" }}>
                                {items.length > 0 ? (
                                    items.map((item: any, index: number) => {
                                        const rowId = item.id;
                                        const accessoriesTotal = Array.isArray(item.accessories)
                                            ? item.accessories.reduce(
                                                (sum: number, acc: any) =>
                                                    sum + Number(acc.total_amount || 0),
                                                0
                                            )
                                            : 0;
                                        const accessoriesCount = Array.isArray(item.accessories)
                                            ? item.accessories.length
                                            : 0;
                                        const isLocked = item.challan_status || item.diff_status === "LOCKED";

                                        return (
                                            <tr key={item.id}>
                                                <td style={{ width: "60px", textAlign: "center" }}>
                                                    {index + 1}
                                                    {(item.challan_status || item.diff_status === "LOCKED") && (
                                                        <Tooltip title="Challan generated for this service">
                                                            <span className="badge bg-secondary ms-1" style={{ fontSize: "9px" }}>
                                                                Locked
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                                <td style={{ width: "120px", textAlign: "center" }}>{item?.model_name || "-"}</td>
                                                <td style={{ width: "200px", textAlign: "center", verticalAlign: "middle" }}>
                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                        <span>{item?.item_name || item?.serviceName}</span>
                                                        <InfoIcon
                                                            style={{
                                                                cursor: "pointer",
                                                                color: tableStyles?.primaryColor || MecordColors?.ThemeBGColor,
                                                                fontSize: "18px",
                                                                flexShrink: 0,
                                                            }}
                                                            onClick={() => {
                                                                const content = item?.prod_description ?? item?.description ?? "";
                                                                setDescriptionModalContent(content);
                                                                setDescriptionModalServiceName(item?.item_name || item?.serviceName || "");
                                                                setShowDescriptionModal(true);
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                                <td style={{ width: "60px", textAlign: "center", verticalAlign: "middle" }}>
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <EditIcon
                                                            style={{
                                                                cursor: isLocked ? "not-allowed" : "pointer",
                                                                color: isLocked ? "#6c757d" : (tableStyles?.primaryColor || MecordColors?.ThemeBGColor),
                                                                fontSize: "20px",
                                                            }}
                                                            onClick={() => {
                                                                if (isLocked) return;
                                                                setAdditionalDetailsIndex(index);
                                                                setAdditionalDetailsDraft(item?.additional_details ?? "");
                                                                setShowAdditionalDetailsModal(true);
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                                {/* Serial No. - Editable */}
                                                <td style={{ width: "120px", textAlign: "center" }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "2px",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "4px",
                                                                position: "relative",
                                                            }}
                                                        >
                                                            <Form.Control
                                                                type="text"
                                                                disabled={isLocked}
                                                                value={item?.serialNo || item?.serial_number || item?.serial_no || ""}
                                                                onChange={(e) => {
                                                                    handleItemUpdate(index, "serialNo", e.target.value);
                                                                    // Clear error when user starts typing
                                                                    if (e.target.value.trim() !== "") {
                                                                        setSerialNoErrors((prev) => {
                                                                            const newErrors = { ...prev };
                                                                            delete newErrors[index];
                                                                            return newErrors;
                                                                        });
                                                                    }
                                                                }}
                                                                placeholder="Enter Serial No."
                                                                size="sm"
                                                                isInvalid={!!serialNoErrors[index]}
                                                                style={{
                                                                    ...commonInputStyle,
                                                                    flex: 1,
                                                                    paddingRight: (item?.serialNo || item?.serial_number || item?.serial_no) && (item?.serialNo || item?.serial_number || item?.serial_no).trim() !== "" ? "32px" : "8px",
                                                                    borderColor: serialNoErrors[index] ? "#dc3545" : undefined
                                                                }}
                                                                className="form-control-placeholder"
                                                            />
                                                            {(item?.serialNo || item?.serial_number || item?.serial_no) && (item?.serialNo || item?.serial_number || item?.serial_no) !== "-" && (item?.serialNo || item?.serial_number || item?.serial_no).trim() !== "" && (
                                                                <InfoIcon
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        color: MecordColors?.ThemeBGColor || "#007bff",
                                                                        fontSize: "18px",
                                                                        position: "absolute",
                                                                        right: "8px",
                                                                        zIndex: 10,
                                                                    }}
                                                                    onClick={() => fetchProductHistory(item.serialNo || item.serial_number || item.serial_no)}
                                                                />
                                                            )}
                                                        </div>
                                                        {serialNoErrors[index] && (
                                                            <small className="text-danger" style={{ fontSize: "11px", display: "block", margin: "2px 0 0 0", lineHeight: "1.2", width: "100%", fontFamily: "inherit" }}>
                                                                {serialNoErrors[index]}
                                                            </small>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* SAC Code - Autocomplete Dropdown */}
                                                <td style={{ width: "120px", textAlign: "center" }}>
                                                    {isLocked ? (
                                                        item.sac_code || "-"
                                                    ) : (
                                                        <div style={{ position: "relative", width: "100%" }}>
                                                            <Autocomplete
                                                                options={sacCodeOptions}
                                                                getOptionLabel={(option) => {
                                                                    if (typeof option === "string") return option;
                                                                    return option?.sac_code?.toString() || "";
                                                                }}
                                                                filterOptions={(options) => options}
                                                                disableClearable
                                                                // 2. Simplified value finding
                                                                value={
                                                                    sacCodeOptions.find((s: any) => {
                                                                        const optionId = s.sac_code_id || s.id;
                                                                        const itemId = item.sac_code_id || item.id;

                                                                        // Exact and Cross-match check
                                                                        return (
                                                                            (s.sac_code && item.sac_code && s.sac_code === item.sac_code) ||
                                                                            (s.id && item.id && s.id === item.id) ||
                                                                            (s.sac_code_id && item.id && s.sac_code_id === item.id) ||
                                                                            (s.id && item.sac_code_id && s.id === item.sac_code_id)
                                                                        );
                                                                    }) || null
                                                                }
                                                                isOptionEqualToValue={(option: any, value: any) => {
                                                                    // console.log("option => in servicesection", option)
                                                                    // console.log("value => in servicesection", value)
                                                                    if (!option || !value) return false;
                                                                    const oId = option.sac_code_id || option.id;
                                                                    const vId = value.sac_code_id || value.id;
                                                                    return oId === vId;
                                                                }}
                                                                // 4. Ensure no filtering happens on selection
                                                                filterSelectedOptions={false}
                                                                clearOnBlur={false}
                                                                onChange={(event, newValue: any) => {
                                                                    // console.log("newValue => in servicesection", newValue)
                                                                    // console.log("event => in servicesection", event)
                                                                    if (newValue) {
                                                                        // Map GST from SAC code
                                                                        const gstFromSac = newValue.gst?.gst_percentage ||
                                                                            newValue.gst_percentage ||
                                                                            (newValue.gst_id && gstOptions.find((g: any) => g.gst_id === newValue.gst_id)?.gst_percentage) ||
                                                                            0;

                                                                        const gstPercent = shouldZeroGstForCompany ? 0 : Number(gstFromSac);
                                                                        const gstOption = (gstFromSac > 0) ? (gstOptions.find((g: any) =>
                                                                            Number(g.gst_percentage) === Number(gstFromSac)
                                                                        ) || gstOptions.find((g: any) => g.gst_id === newValue.gst_id)) : null;

                                                                        // Update all fields in one call
                                                                        handleItemUpdate(index, {
                                                                            sac_code: newValue.sac_code || "",
                                                                            sac_code_id: newValue.sac_code_id || newValue.id || "",
                                                                            gst_percent: gstPercent,
                                                                            gst_percentage: Number(gstFromSac),
                                                                            original_gst_percent: Number(gstFromSac),
                                                                            gst_id: gstOption ? (gstOption.gst_id || "") : ""
                                                                        });

                                                                        // Clear error when SAC code is selected
                                                                        setSacCodeErrors((prev) => {
                                                                            const newErrors = { ...prev };
                                                                            delete newErrors[index];
                                                                            return newErrors;
                                                                        });
                                                                    } else {
                                                                        // If SAC code is cleared, clear SAC and GST fields
                                                                        handleItemUpdate(index, {
                                                                            sac_code: "",
                                                                            sac_code_id: "",
                                                                            gst_percent: 0,
                                                                            gst_percentage: 0,
                                                                            original_gst_percent: 0,
                                                                            gst_id: ""
                                                                        });

                                                                        // Set error when SAC code is cleared
                                                                        // setSacCodeErrors((prev) => ({
                                                                        //     ...prev,
                                                                        //     [index]: "SAC Code is required"
                                                                        // }));
                                                                    }
                                                                }}
                                                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                                                renderOption={(props, option, { selected }) => {
                                                                    const { key, ...otherProps } = props;
                                                                    return (
                                                                        <li key={key} {...otherProps} style={{ padding: 0 }}>
                                                                            <Box
                                                                                sx={{
                                                                                    px: 1.5,
                                                                                    py: 0.8,
                                                                                    width: "100%",
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center",
                                                                                    cursor: "pointer",
                                                                                    "&:hover": {
                                                                                        backgroundColor: MecordColors.Hover,
                                                                                        color: MecordColors.ActiveText,
                                                                                    },
                                                                                    // This matches your Customer options logic perfectly
                                                                                    ...(selected && {
                                                                                        backgroundColor: MecordColors.ActiveBackground,
                                                                                        color: MecordColors.ActiveText,
                                                                                    }),
                                                                                }}
                                                                            >
                                                                                <span>
                                                                                    {typeof option === "string" ? option : (option.sac_code || "")}
                                                                                </span>
                                                                            </Box>
                                                                        </li>
                                                                    );
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder="Select SAC Code"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        error={!!sacCodeErrors[index]}
                                                                        helperText={sacCodeErrors[index] || ""}
                                                                        required
                                                                        sx={{
                                                                            ...autocompleteDropdownStyles.inputSx,
                                                                            ...getDisabledInputStyles(isLocked),
                                                                            height: "31px",
                                                                            "& .MuiInputBase-root": {
                                                                                height: "31px",
                                                                                paddingRight: "32px !important",
                                                                                paddingLeft: "14px !important",
                                                                                fontSize: "13px",
                                                                                fontFamily: "inherit",
                                                                            },
                                                                            "& .MuiAutocomplete-endAdornment": {
                                                                                right: "8px",
                                                                                padding: "0",
                                                                                "& .MuiSvgIcon-root": {
                                                                                    fontSize: "18px",
                                                                                },
                                                                            },
                                                                            "& .MuiFormHelperText-root": {
                                                                                margin: "2px 0 0 0",
                                                                                fontSize: "11px",
                                                                                lineHeight: "1.2",
                                                                                width: "100%",
                                                                                maxWidth: "100%",
                                                                                color: "#dc3545", // Bootstrap's text-danger color
                                                                                fontFamily: "inherit",
                                                                                "&.Mui-error": {
                                                                                    color: "#dc3545", // Ensure error state uses Bootstrap danger color
                                                                                },
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ width: "150px", textAlign: "center" }}>{item?.serviceCategory || "-"}</td>
                                                <td style={{ width: "120px", textAlign: "center" }}>{item?.serviceMethod || "-"}</td>
                                                <td style={{ width: "120px", textAlign: "center" }}>{item?.serviceType || "-"}</td>
                                                <td style={{ width: "120px", textAlign: "center" }}>{item?.executionMode || "-"}</td>
                                                <td style={{ width: "120px", textAlign: "center" }}>{item?.productQuantity || item?.quantity}</td>

                                                {/* Quantity Editable */}
                                                {/* <td>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                value={item.productQuantity || item.quantity}
                                                // Always editable
                                                onChange={(e) =>
                                                    handleItemUpdate(index, "productQuantity", e.target.value)
                                                }
                                                size="sm"
                                                style={commonInputStyle}
                                                className="form-control-placeholder"
                                            />
                                        </td> */}

                                                {/* <td>{item?.unit_name || item?.unit || "-"}</td> */}

                                                {/* Service Rate Editable */}
                                                <td style={{ width: "120px", textAlign: "center" }}>
                                                    <div className="d-flex align-items-center">
                                                        <span className="me-1">₹</span>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={item.serviceRate} // Use serviceRate
                                                            disabled={isLocked}
                                                            onChange={(e) =>
                                                                handleItemUpdate(index, "serviceRate", e.target.value)
                                                            }
                                                            size="sm"
                                                            style={{ ...commonInputStyle, paddingLeft: "5px", width: "100%" }}
                                                            className="form-control-placeholder"
                                                        />
                                                    </div>
                                                </td>

                                                {/* Gross Value (Qty * Service Rate or Qty * Rate in Currency) */}
                                                <td style={{ width: "120px", textAlign: "center" }}>
                                                    {(() => {
                                                        const currencySymbol = currency_symbol || "₹";
                                                        // Calculate gross value based on currency
                                                        // If currency is not INR, use rate_in_currency; otherwise use serviceRate
                                                        let grossValue = 0;
                                                        if (currency_code && currency_code !== "INR" && currency_code !== "IND") {
                                                            // For foreign currency, use rate_in_currency
                                                            const rateInCurrency = item.rate_in_currency
                                                                ? Number(item.rate_in_currency)
                                                                : (item.exchange_rate && Number(item.exchange_rate) > 0
                                                                    ? (Number(item.serviceRate || item.unit_price || 0) / Number(item.exchange_rate))
                                                                    : Number(item.serviceRate || item.unit_price || 0));
                                                            grossValue = Number(item.productQuantity || item.quantity || 0) * rateInCurrency;
                                                        } else {
                                                            // For INR, use serviceRate
                                                            grossValue = Number(item.productQuantity || item.quantity || 0) * Number(item.serviceRate || item.unit_price || 0);
                                                        }
                                                        return `${currencySymbol}${Number(grossValue || 0).toFixed(2)}`;
                                                    })()}
                                                </td>

                                                {/* Total Service Amount (Qty * Rate) */}


                                                {/* Discount/Flat */}
                                                <td style={{ width: "180px", textAlign: "center" }}>
                                                    <div className="d-flex align-items-center" style={{ gap: "4px" }}>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.discount_value || 0}
                                                            min="0"
                                                            max={(item.discount_type === "%" || item.discount_type === "percentage" || item.discount_type === "percent") ? 100 : undefined}
                                                            disabled={isLocked}
                                                            onChange={(e) => {
                                                                const inputValue = Number(e.target.value) || 0;
                                                                // Calculate gross value for validation
                                                                const qty = Number(item.productQuantity || item.quantity) || 0;
                                                                let grossValue = 0;
                                                                if (currency_code && currency_code !== "INR" && currency_code !== "IND") {
                                                                    const rateInCurrency = item.rate_in_currency
                                                                        ? Number(item.rate_in_currency)
                                                                        : (item.exchange_rate && Number(item.exchange_rate) > 0
                                                                            ? (Number(item.serviceRate || item.unit_price || 0) / Number(item.exchange_rate))
                                                                            : Number(item.serviceRate || item.unit_price || 0));
                                                                    grossValue = qty * rateInCurrency;
                                                                } else {
                                                                    grossValue = qty * Number(item.serviceRate || item.unit_price || 0);
                                                                }

                                                                // Validate based on discount type
                                                                if (item.discount_type === "%") {
                                                                    // Validate: If discount_type is "%", cap at 100%
                                                                    if (inputValue > 100) {
                                                                        toast.warning("Discount percentage cannot exceed 100%");
                                                                        handleItemUpdate(index, "discount_value", 100);
                                                                    } else {
                                                                        handleItemUpdate(index, "discount_value", inputValue);
                                                                    }
                                                                } else {
                                                                    // Validate: currency discount cannot exceed gross value
                                                                    if (inputValue > grossValue) {
                                                                        toast.warning("Discount amount cannot exceed gross value");
                                                                        handleItemUpdate(index, "discount_value", grossValue);
                                                                    } else {
                                                                        handleItemUpdate(index, "discount_value", inputValue);
                                                                    }
                                                                }
                                                            }}
                                                            style={{ width: "70px" }}
                                                            size="sm"
                                                        />
                                                        <Form.Select
                                                            value={(item.discount_type === "%" || item.discount_type === "percentage" || item.discount_type === "percent") ? "%" : (currency_symbol || "₹")}
                                                            disabled={isLocked}
                                                            onChange={(e) => {
                                                                const newDiscountType = e.target.value;
                                                                // Reset discount value to 0 when changing type
                                                                handleItemUpdate(index, {
                                                                    discount_type: newDiscountType,
                                                                    discount_value: 0
                                                                });
                                                            }}
                                                            style={{ width: "70px" }}
                                                            size="sm"
                                                        >
                                                            <option value="%">%</option>
                                                            <option value={currency_symbol || "₹"}>{currency_symbol || "₹"}</option>
                                                        </Form.Select>
                                                    </div>
                                                </td>

                                                {/* Discount Value */}
                                                <td style={{ width: "110px", textAlign: "center" }}>
                                                    {(() => {
                                                        const currencySymbol = currency_symbol || "₹";
                                                        const isPercent = item.discount_type === "%" || item.discount_type === "percentage" || item.discount_type === "percent";
                                                        const discVal = Number(item.discount_value ?? item.discount ?? 0);
                                                        const qty = Number(item.productQuantity ?? item.quantity ?? 1);
                                                        const rate = Number(item.serviceRate ?? item.unit_price ?? 0);
                                                        const gross = Number(item.gross_value) || (qty * rate);
                                                        const discountAmount = isPercent
                                                            ? (gross > 0 ? (gross * discVal) / 100 : 0)
                                                            : discVal;
                                                        const displayVal = item.discount_amount != null ? Number(item.discount_amount) : discountAmount;
                                                        return `${currencySymbol}${Number(displayVal || 0).toFixed(2)}`;
                                                    })()}
                                                </td>
                                                <td style={{ width: "100px", textAlign: "center" }}>
                                                    {(() => {
                                                        const isPercent = item.discount_type === "%" || item.discount_type === "percentage" || item.discount_type === "percent";
                                                        const discVal = Number(item.discount_value ?? item.discount ?? 0);
                                                        const qty = Number(item.productQuantity ?? item.quantity ?? 1);
                                                        const rate = Number(item.serviceRate ?? item.unit_price ?? 0);
                                                        const gross = Number(item.gross_value) || (qty * rate);
                                                        const discountAmount = isPercent
                                                            ? (gross > 0 ? (gross * discVal) / 100 : 0)
                                                            : discVal;
                                                        const net = item.net_value != null && Number(item.net_value) > 0
                                                            ? Number(item.net_value)
                                                            : (item.total_service_net_amount != null && Number(item.total_service_net_amount) > 0
                                                                ? Number(item.total_service_net_amount)
                                                                : Math.max(0, gross - discountAmount));
                                                        return `₹${Number(net || 0).toFixed(2)}`;
                                                    })()}
                                                </td>

                                                {/* GST % - Autocomplete Dropdown */}
                                                <td style={{ width: "170px", textAlign: "center" }}>
                                                    <Autocomplete
                                                        disableClearable
                                                        options={gstOptions}
                                                        filterOptions={(options) => options}
                                                        getOptionLabel={(option: any) =>
                                                            typeof option === "string"
                                                                ? option
                                                                : `${option.gst_percentage}%`
                                                        }
                                                        value={
                                                            gstOptions.find(
                                                                (g: any) =>
                                                                    Number(g.gst_percentage) === Number(item.gst_percentage || item.gst_percent || 0) ||
                                                                    g.gst_id === item.gst_id
                                                            ) || null
                                                        }
                                                        disabled={shouldZeroGstForCompany || isLocked}
                                                        onChange={(event, newValue: any) => {
                                                            // console.log("newValue => in servicesection", newValue)
                                                            // console.log("event => in servicesection", event)
                                                            if (!shouldZeroGstForCompany && newValue) {
                                                                const gstPercent = Number(newValue.gst_percentage) || 0;
                                                                handleItemUpdate(index, {
                                                                    gst_percent: gstPercent,
                                                                    gst_percentage: gstPercent,
                                                                    original_gst_percent: gstPercent,
                                                                    gst_id: newValue.gst_id || ""
                                                                });
                                                            } else if (!newValue) {
                                                                handleItemUpdate(index, {
                                                                    gst_percent: 0,
                                                                    gst_percentage: 0,
                                                                    original_gst_percent: 0,
                                                                    gst_id: ""
                                                                });
                                                            }
                                                        }}
                                                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                                        renderOption={(props, option) =>
                                                            autocompleteDropdownStyles.renderOption(
                                                                props,
                                                                option,
                                                                (opt) =>
                                                                    typeof opt === "string"
                                                                        ? opt
                                                                        : String(opt.gst_percentage || "")
                                                            )
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Select GST"
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={shouldZeroGstForCompany || isLocked}
                                                                sx={{
                                                                    ...autocompleteDropdownStyles.inputSx,
                                                                    ...getDisabledInputStyles(shouldZeroGstForCompany || isLocked),
                                                                    "& .MuiOutlinedInput-root": {
                                                                        height: "27px",
                                                                        width: "100%",
                                                                        fontSize: "13px",
                                                                        fontFamily: "Poppins, sans-serif",
                                                                        "& .MuiOutlinedInput-input": {
                                                                            padding: "6px 32px 6px 14px",
                                                                        },
                                                                        "& .MuiAutocomplete-endAdornment": {
                                                                            right: "8px",
                                                                            "& .MuiIconButton-root": {
                                                                                padding: "4px",
                                                                                "& svg": {
                                                                                    fontSize: "18px",
                                                                                },
                                                                            },
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </td>


                                                {/* SGST / CGST  OR  IGST */}
                                                {isIntraState ? (
                                                    <>
                                                        <td style={{ width: "100px", textAlign: "center" }}>
                                                            <Form.Control
                                                                value={item.sgst_percent != null && item.sgst_percent !== "" ? item.sgst_percent : ((Number(item.gst_percentage || item.gst_percent || 0) / 2) || "-")}
                                                                disabled
                                                                size="sm"
                                                                style={{ width: "100%" }}
                                                            />
                                                        </td>
                                                        <td style={{ width: "100px", textAlign: "center" }}>
                                                            <Form.Control
                                                                value={item.cgst_percent != null && item.cgst_percent !== "" ? item.cgst_percent : ((Number(item.gst_percentage || item.gst_percent || 0) / 2) || "-")}
                                                                disabled
                                                                size="sm"
                                                                style={{ width: "100%" }}
                                                            />
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td style={{ width: "100px", textAlign: "center" }}>
                                                        <Form.Control
                                                            value={item.igst_percent != null && item.igst_percent !== "" ? item.igst_percent : ((Number(item.gst_percentage || item.gst_percent || 0)) || "-")}
                                                            disabled
                                                            size="sm"
                                                            style={{ width: "100%" }}
                                                        />
                                                    </td>
                                                )}

                                                {/* GST Value */}
                                                <td style={{ width: "100px", textAlign: "center" }}>
                                                    {(() => {
                                                        const isPercent = item.discount_type === "%" || item.discount_type === "percentage" || item.discount_type === "percent";
                                                        const discVal = Number(item.discount_value ?? item.discount ?? 0);
                                                        const qty = Number(item.productQuantity ?? item.quantity ?? 1);
                                                        const rate = Number(item.serviceRate ?? item.unit_price ?? 0);
                                                        const gross = Number(item.gross_value) || (qty * rate);
                                                        const discountAmount = isPercent
                                                            ? (gross > 0 ? (gross * discVal) / 100 : 0)
                                                            : discVal;
                                                        const net = item.net_value != null && Number(item.net_value) > 0
                                                            ? Number(item.net_value)
                                                            : (item.total_service_net_amount != null && Number(item.total_service_net_amount) > 0
                                                                ? Number(item.total_service_net_amount)
                                                                : Math.max(0, gross - discountAmount));
                                                        const gstPct = shouldZeroGstForCompany ? 0 : Number(item.gst_percentage || item.gst_percent || 0);
                                                        const gstValue = item.gst_value != null && Number(item.gst_value) > 0
                                                            ? Number(item.gst_value)
                                                            : (net * gstPct) / 100;
                                                        return `₹${Number(gstValue || 0).toFixed(2)}`;
                                                    })()}
                                                </td>

                                                {/* Final Total - Service */}
                                                <td style={{ width: "160px", textAlign: "center" }}>
                                                    {(() => {
                                                        const isPercent = item.discount_type === "%" || item.discount_type === "percentage" || item.discount_type === "percent";
                                                        const discVal = Number(item.discount_value ?? item.discount ?? 0);
                                                        const qty = Number(item.productQuantity ?? item.quantity ?? 1);
                                                        const rate = Number(item.serviceRate ?? item.unit_price ?? 0);
                                                        const gross = Number(item.gross_value) || (qty * rate);
                                                        const discountAmount = isPercent
                                                            ? (gross > 0 ? (gross * discVal) / 100 : 0)
                                                            : discVal;
                                                        const net = item.net_value != null && Number(item.net_value) > 0
                                                            ? Number(item.net_value)
                                                            : (item.total_service_net_amount != null && Number(item.total_service_net_amount) > 0
                                                                ? Number(item.total_service_net_amount)
                                                                : Math.max(0, gross - discountAmount));
                                                        const gstPct = shouldZeroGstForCompany ? 0 : Number(item.gst_percentage || item.gst_percent || 0);
                                                        const gstValue = item.gst_value != null && Number(item.gst_value) > 0
                                                            ? Number(item.gst_value)
                                                            : (net * gstPct) / 100;
                                                        const total = item.total_amount != null && Number(item.total_amount) > 0
                                                            ? Number(item.total_amount)
                                                            : (item.totalServiceAmount != null && Number(item.totalServiceAmount) > 0
                                                                ? Number(item.totalServiceAmount)
                                                                : (item.total_service_amount != null && Number(item.total_service_amount) > 0
                                                                    ? Number(item.total_service_amount)
                                                                    : (net + gstValue)));
                                                        return <strong>₹{Number(total || 0).toFixed(2)}</strong>;
                                                    })()}
                                                </td>

                                                {/* Total Accessories Amount + view/add icons */}
                                                <td style={{ width: "180px", textAlign: "center", verticalAlign: "middle" }}>
                                                    <div className="d-flex flex-column align-items-center gap-1">
                                                        <strong>
                                                            ₹{Number(accessoriesTotal || 0).toFixed(2)}
                                                        </strong>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <InfoIcon
                                                                titleAccess={`View Accessories (${accessoriesCount})`}
                                                                style={{
                                                                    cursor: "pointer",
                                                                    color: tableStyles?.primaryColor || MecordColors?.ThemeBGColor,
                                                                    fontSize: "18px",
                                                                }}
                                                                onClick={() => handleOpenViewAccessories(index)}
                                                            />
                                                            <AddCircleOutlineIcon
                                                                titleAccess={isLocked ? "Accessories Locked (Challan Generated)" : "Add / Edit Accessories"}
                                                                style={{
                                                                    cursor: isLocked ? "not-allowed" : "pointer",
                                                                    color: isLocked ? "#6c757d" : (tableStyles?.primaryColor || MecordColors?.ThemeBGColor),
                                                                    fontSize: "18px",
                                                                }}
                                                                onClick={() => {
                                                                    if (isLocked) {
                                                                        toast.warning("Accessories cannot be modified because a Challan has already been generated.");
                                                                        return;
                                                                    }
                                                                    handleOpenAddAccessories(index);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>

                                                <td style={{ width: "250px", textAlign: "center" }}>
                                                    {(() => {
                                                        // Handle serviceAddress as object
                                                        if (item.serviceAddress && typeof item.serviceAddress === 'object' && !Array.isArray(item.serviceAddress)) {
                                                            const addr = item.serviceAddress;
                                                            if (addr.address) {
                                                                const addressParts = [
                                                                    addr.address,
                                                                    addr.city,
                                                                    addr.state,
                                                                    addr.country
                                                                ].filter(Boolean);
                                                                const pincode = addr.pincode || addr.pin_code;
                                                                if (addressParts.length > 0) {
                                                                    return pincode ? `${addressParts.join(", ")} - ${pincode}` : addressParts.join(", ");
                                                                }
                                                            }
                                                        }

                                                        // Handle serviceAddress as string (legacy)
                                                        if (typeof item.serviceAddress === 'string' && item.serviceAddress) {
                                                            return item.serviceAddress;
                                                        }

                                                        // Fallback: if serviceAddress is not set, try to format from shipping_address
                                                        if (item.shipping_address) {
                                                            let addr: any = null;

                                                            // Handle object case
                                                            if (typeof item.shipping_address === "object" && !Array.isArray(item.shipping_address)) {
                                                                addr = item.shipping_address;
                                                            }
                                                            // Handle array case
                                                            else if (Array.isArray(item.shipping_address) && item.shipping_address.length > 0) {
                                                                addr = item.shipping_address[0];
                                                            }

                                                            if (addr && addr.address) {
                                                                const addressParts = [
                                                                    addr.address,
                                                                    addr.city,
                                                                    addr.state,
                                                                    addr.country
                                                                ].filter(Boolean);
                                                                const pincode = addr.pincode || addr.pin_code;
                                                                if (addressParts.length > 0) {
                                                                    return pincode ? `${addressParts.join(", ")} - ${pincode}` : addressParts.join(", ");
                                                                }
                                                            }
                                                        }
                                                        // If shipping_address_id exists, try to find it in shippingAddresses
                                                        if (item.shipping_address_id && shippingAddresses && Array.isArray(shippingAddresses)) {
                                                            const foundAddr = shippingAddresses.find((a: any) => (a.shipping_address_id || a.id) === item.shipping_address_id);
                                                            if (foundAddr && foundAddr.address) {
                                                                const addressParts = [
                                                                    foundAddr.address,
                                                                    foundAddr.city,
                                                                    foundAddr.state,
                                                                    foundAddr.country
                                                                ].filter(Boolean);
                                                                const pincode = foundAddr.pincode || foundAddr.pin_code;
                                                                if (addressParts.length > 0) {
                                                                    return pincode ? `${addressParts.join(", ")} - ${pincode}` : addressParts.join(", ");
                                                                }
                                                            }
                                                        }
                                                        return "-";
                                                    })()}
                                                </td>
                                                <td style={{ width: "200px", textAlign: "center" }}>
                                                    {item.reportedComplaint && item.reportedComplaint.trim() !== "" ? (
                                                        <Tooltip
                                                            title={item.reportedComplaint}
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <div
                                                                style={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    maxWidth: "150px",
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                {item.reportedComplaint}
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>

                                                {/* Actions */}
                                                <td
                                                    style={{
                                                        position: "sticky",
                                                        right: 0,
                                                        backgroundColor: "white",
                                                        borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                                                        zIndex: 10,
                                                        width: "80px",
                                                        textAlign: "center",
                                                        verticalAlign: "middle",
                                                    }}
                                                >
                                                    {isLocked ? (
                                                        <Badge bg="secondary" style={{ fontSize: "11px" }}>Locked</Badge>
                                                    ) : (
                                                        <DynamicMenuItem
                                                            row={index}
                                                            menuItems={[
                                                                {
                                                                    label: "Delete",
                                                                    icon: DeleteOutlineIcon,
                                                                    onClick: (idx: any) => handleDelete(idx),
                                                                    color: "error",
                                                                    textColor: "#dc3545",
                                                                },
                                                            ]}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : null}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-center text-muted py-4 border rounded p-3 bg-white">
                        <img
                            src={NoSelectedProducts}
                            alt="No services selected"
                            className="mb-3"
                            style={{ maxWidth: "180px", height: "auto" }}
                        />
                        <div>Search existing services to add to this list</div>
                    </div>
                )}
            </div>

            {/* Product History Modal */}
            <ProductHistoryModal
                show={showHistoryModal}
                onHide={() => {
                    setShowHistoryModal(false);
                    setProductHistory(null);
                    setSelectedSerialNo("");
                }}
                serialNo={selectedSerialNo}
                productHistory={productHistory}
                loading={loadingHistory}
            />

            <AddServiceAccessoriesModal
                show={showAddAccessoriesModal}
                onHide={() => {
                    setShowAddAccessoriesModal(false);
                    setAccessoriesServiceIndex(null);
                }}
                serviceName={
                    accessoriesServiceIndex !== null
                        ? items[accessoriesServiceIndex]?.item_name ||
                          items[accessoriesServiceIndex]?.product_name ||
                          ""
                        : ""
                }
                serviceCategory={
                    accessoriesServiceIndex !== null
                        ? items[accessoriesServiceIndex]?.serviceCategory || ""
                        : ""
                }
                gstOptions={gstOptions}
                existingAccessories={
                    accessoriesServiceIndex !== null
                        ? items[accessoriesServiceIndex]?.accessories || []
                        : []
                }
                onAddAccessories={handleSaveAccessories}
                currencySymbol={currency_symbol || "₹"}
                isCompositional={shouldZeroGstForCompany}
                isIntraState={isIntraState}
            />

            <ViewServiceAccessoriesModal
                show={showViewAccessoriesModal}
                onHide={() => {
                    setShowViewAccessoriesModal(false);
                    setAccessoriesServiceIndex(null);
                }}
                serviceName={
                    accessoriesServiceIndex !== null
                        ? items[accessoriesServiceIndex]?.item_name ||
                          items[accessoriesServiceIndex]?.product_name ||
                          ""
                        : ""
                }
                serviceCategory={
                    accessoriesServiceIndex !== null
                        ? items[accessoriesServiceIndex]?.serviceCategory || ""
                        : ""
                }
                accessories={
                    accessoriesServiceIndex !== null
                        ? items[accessoriesServiceIndex]?.accessories || []
                        : []
                }
                currencySymbol={currency_symbol || "₹"}
                isIntraState={isIntraState}
            />

            {/* Add Shipping Address Modal */}
            <AddShippingAddressModal
                show={showShippingAddressModal}
                onHide={() => {
                    setShowShippingAddressModal(false);
                }}
                companyId={getEffectiveCompanyId()}
                formikValues={company || {}}
                setFieldValue={(field: string, value: any) => {
                    // No-op for ServiceSection since we don't have formik
                    // The parent component should handle formik updates
                }}
                onAddressAdded={(newAddress) => {
                    // Auto-select the newly added address - store full object
                    if (newAddress) {
                        const effectiveCompanyId = getEffectiveCompanyId();
                        const fullAddress = {
                            shipping_address_id: newAddress.shipping_address_id || newAddress.id,
                            company_id: newAddress.company_id || effectiveCompanyId,
                            address: newAddress.address,
                            city: newAddress.city,
                            state: newAddress.state,
                            country: newAddress.country,
                            pincode: newAddress.pincode || newAddress.pin_code,
                            pin_code: newAddress.pincode || newAddress.pin_code,
                            phone_number: newAddress.phone_number,
                            phone_country: newAddress.phone_country || "+91",
                            is_default: newAddress.is_default || false,
                            created_at: newAddress.created_at,
                            updated_at: newAddress.updated_at
                        };

                        // Add to serviceAddressOptions if not already present
                        const addressId = fullAddress.shipping_address_id;
                        const exists = serviceAddressOptions.find(
                            (opt) => (opt.shipping_address_id || opt.id) === addressId
                        );

                        if (!exists) {
                            const newOption = {
                                id: addressId,
                                shipping_address_id: addressId,
                                name: `${fullAddress.address}, ${fullAddress.city}, ${fullAddress.state}, ${fullAddress.country} - ${fullAddress.pincode}`,
                                address: fullAddress.address,
                                city: fullAddress.city,
                                state: fullAddress.state,
                                country: fullAddress.country,
                                pincode: fullAddress.pincode,
                                phone_number: fullAddress.phone_number,
                                phone_country: fullAddress.phone_country,
                                company_id: fullAddress.company_id,
                                is_default: fullAddress.is_default,
                                created_at: fullAddress.created_at,
                                updated_at: fullAddress.updated_at,
                                fullAddress: fullAddress
                            };
                            setServiceAddressOptions((prev) => [...prev, newOption]);
                        }

                        // Auto-select the newly added address
                        setFormData((prev) => ({
                            ...prev,
                            serviceAddress: fullAddress,
                            shipping_address_id: addressId,
                            shipping_address: fullAddress,
                        }));

                        // Clear any validation errors
                        if (errors.serviceAddress) {
                            setErrors((prev) => ({ ...prev, serviceAddress: "" }));
                        }

                        toast.success("Shipping address added and selected successfully!");
                    }
                }}
            />

            {/* Service Description Modal (Info icon) */}
            <Modal
                show={showDescriptionModal}
                onHide={() => {
                    setShowDescriptionModal(false);
                    setDescriptionModalContent("");
                    setDescriptionModalServiceName("");
                }}
                centered
            >
                <Modal.Header
                    closeButton
                    style={{
                        fontFamily: tableStyles?.header?.fontFamily,
                        fontSize: tableStyles?.header?.fontSize,
                        borderBottom: "1px solid #dee2e6",
                    }}
                >
                    <Modal.Title
                        style={{
                            fontFamily: tableStyles?.header?.fontFamily,
                            fontSize: tableStyles?.header?.fontSize,
                            fontWeight: tableStyles?.header?.fontWeight,
                        }}
                    >
                        Service Description
                        {descriptionModalServiceName ? ` - ${descriptionModalServiceName}` : ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        fontFamily: filterPopoverStyles?.fontFamily,
                        fontSize: filterPopoverStyles?.fontSize,
                    }}
                >
                    {descriptionModalContent ? (
                        <div
                            className={DETAILS_BG}
                            style={{
                                minHeight: "80px",
                                fontFamily: filterPopoverStyles?.fontFamily,
                                fontSize: filterPopoverStyles?.fontSize,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(descriptionModalContent),
                            }}
                        />
                    ) : (
                        <div
                            className="text-muted"
                            style={{
                                fontFamily: filterPopoverStyles?.fontFamily,
                                fontSize: filterPopoverStyles?.fontSize,
                            }}
                        >
                            No description available.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button
                        variant="secondary"
                        className="rounded"
                        style={{
                            fontFamily: filterPopoverStyles?.button?.fontFamily,
                            fontSize: filterPopoverStyles?.button?.fontSize,
                            borderColor: filterPopoverStyles?.cancelButton?.borderColor,
                            color: filterPopoverStyles?.cancelButton?.color,
                            backgroundColor: "#fff",
                        }}
                        onClick={() => {
                            setShowDescriptionModal(false);
                            setDescriptionModalContent("");
                            setDescriptionModalServiceName("");
                        }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Additional Details Modal (Pencil icon) */}
            <Modal
                show={showAdditionalDetailsModal}
                onHide={() => {
                    setShowAdditionalDetailsModal(false);
                    setAdditionalDetailsIndex(null);
                    setAdditionalDetailsDraft("");
                }}
                centered
                className="modal-lg"
            >
                <Modal.Header
                    closeButton
                    style={{
                        fontFamily: tableStyles?.header?.fontFamily,
                        fontSize: tableStyles?.header?.fontSize,
                        borderBottom: "1px solid #dee2e6",
                    }}
                >
                    <Modal.Title
                        style={{
                            fontFamily: tableStyles?.header?.fontFamily,
                            fontSize: tableStyles?.header?.fontSize,
                            fontWeight: tableStyles?.header?.fontWeight,
                        }}
                    >
                        Additional Details
                        {additionalDetailsIndex !== null && items[additionalDetailsIndex]
                            ? ` - ${items[additionalDetailsIndex]?.item_name || items[additionalDetailsIndex]?.serviceName || ""}`
                            : ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        fontFamily: filterPopoverStyles?.fontFamily,
                        fontSize: filterPopoverStyles?.fontSize,
                    }}
                >
                    <Form.Group className="mb-0">
                        <TextEditor
                            value={additionalDetailsDraft}
                            onChange={setAdditionalDetailsDraft}
                            placeholder="Enter additional details..."
                            minHeightPx={120}
                            maxHeightPx={200}
                            blockImages={true}
                        />
                    </Form.Group>
                    <div className="mb-0 mt-2">
                        <hr className="mb-0 w-100" />
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button
                        variant="secondary"
                        className="px-3 rounded"
                        style={{
                            fontFamily: filterPopoverStyles?.button?.fontFamily,
                            fontSize: filterPopoverStyles?.button?.fontSize,
                            borderColor: filterPopoverStyles?.cancelButton?.borderColor,
                            color: filterPopoverStyles?.cancelButton?.color,
                            backgroundColor: "#fff",
                        }}
                        onClick={() => {
                            setShowAdditionalDetailsModal(false);
                            setAdditionalDetailsIndex(null);
                            setAdditionalDetailsDraft("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        className="px-3 rounded"
                        style={{
                            fontFamily: filterPopoverStyles?.button?.fontFamily,
                            fontSize: filterPopoverStyles?.button?.fontSize,
                            backgroundColor: filterPopoverStyles?.applyButton?.backgroundColor,
                            borderColor: filterPopoverStyles?.applyButton?.backgroundColor,
                            color: "#fff",
                        }}
                        onClick={() => {
                            if (additionalDetailsIndex !== null) {
                                const updatedItems = [...items];
                                (updatedItems[additionalDetailsIndex] as any).additional_details =
                                    additionalDetailsDraft;
                                setItems(updatedItems);
                            }
                            setShowAdditionalDetailsModal(false);
                            setAdditionalDetailsIndex(null);
                            setAdditionalDetailsDraft("");
                        }}
                    >
                        Update
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EnquiryServiceForm;
